import { Timer, Vector3 } from 'three/webgpu';
import {
  AnimState,
  Buttons,
  CALLINGS,
  CROUCH_EYE_HEIGHT,
  DOWNED_DURATION_MS,
  EntityKind,
  Ev,
  ITEMS,
  InteractAction,
  ItemLocation,
  MAPS,
  MARKS,
  MAX_HP,
  MAX_STAMINA,
  MarkTier,
  NPC_TALK_RANGE,
  PLAYER_EYE_HEIGHT,
  PLAYER_HEIGHT,
  PRONE_EYE_HEIGHT,
  REVIVE_RANGE,
  REVIVE_SECONDS,
  RESPAWN_DELAY_MS,
  QUEST_BREATHER_MS,
  VOICES,
  WALK_SPEED,
  STEP_UP,
  SnapFlags,
  Stance,
  TICK_RATE,
  TUTORIAL_MAP_ID,
  TUTORIAL_MODE,
  DEFAULT_MAP_ID,
  YARD,
  TARGET_ACQUIRE_RANGE,
  TARGET_ACQUIRE_RANGE_SQ,
  abilityBySlot,
  clamp,
  needsWaking,
  nextTargetId,
  sortTargetCandidates,
  traceRay,
  unpackLook,
  type TargetCandidate,
  type AbilityDef,
  type GameEvent,
  type HitTarget,
  type ItemStack,
  type MapDef,
  type Vec3,
  type VoiceId,
  type World,
} from '@stillsun/shared';
import { sound } from './audio/sound.js';
import { Requiem } from './audio/requiem.js';
import { SOUND_MANIFEST } from './audio/manifest.js';
import {
  createRenderer,
  onGovernor,
  precompileScene,
  queryParam,
  setLatePrecompileHook,
} from './render/renderer.js';
import { CROWD_BODIES, CrowdField } from './render/crowd.js';
import { Splash } from './ui/splash.js';
import { mountCinematic, type CinematicHandle } from './ui/cinematic.js';
import { createPost } from './render/post.js';
import { createScene } from './render/scene.js';
import { prefetchRegions, setRegionSidecar } from './render/region.js';
import {
  REGION_SPECS,
  applyDownsRegion,
  applyLanternfallRegion,
  applySpineRegion,
  parseRegion,
  regionAssetBase,
  type RegionSidecar,
} from '@stillsun/shared';
import { DecalField } from './render/decals.js';
import { FrameCapture } from './render/capture.js';
import { ExtractBeacons, debugExtracts, type ExtractSnap } from './render/beacons.js';
import { QuestMarker } from './render/questmarker.js';
import { NpcField } from './game/npcs.js';
import { ContainerField, debugContainers } from './render/containers.js';
import { CAM_DIST_MAX, CAM_DIST_MIN, InputController } from './input/input.js';
import { NetClient } from './net/client.js';
import { session } from './net/session.js';
import { progressStore } from './net/progress-store.js';
import { Predictor } from './game/prediction.js';
import { RemoteManager } from './game/remote.js';
import { EntityManager, isEntityId } from './game/entities.js';
import { LootBeams } from './game/lootbeams.js';
import { KillCam, KILLCAM_TOTAL_MS } from './game/killcam.js';
import { FeedbackCollector, flushQueuedFeedback } from './game/feedback.js';
import { AbilityTracker } from './game/abilities.js';
import { AbilityEffects, type RevealPoint } from './game/effects.js';
import { RaidBar, type AbilitySlotView } from './ui/raidbar.js';
import { FloatingNumbers, type FloaterKind } from './ui/floaters.js';
import { KillFeed } from './ui/killfeed.js';
import { Hud, type CompassTarget, type ScoreRow, type TargetView } from './ui/hud.js';
import { QuestPanel } from './ui/quests.js';
import { PartyPanel } from './ui/party.js';
import { ZoneMap } from './ui/zonemap.js';
import { settings } from './ui/settings.js';
import { mountOverlay, type OverlayHandle } from './ui/overlay.js';
import {
  mountHub,
  mountWaking,
  type HubDeployRequest,
  type HubHandle,
  type WakingHandle,
} from './ui/hub/index.js';

const HUD_INTERVAL_MS = 250;
/**
 * The boot JIT probe (a 2e6-iteration loop, best of two) above this many ms means the
 * JavaScript compiler is off — Edge's Enhanced Security Mode. With the JIT it is ~2 ms on a
 * desktop and under 10 on a phone; interpreted it is 40+ (2026-09-05, the owner's Edge).
 */
const JIT_OFF_MS = 15;

// ---------------------------------------------------------------- the boom ---
/**
 * The third-person camera (plan §1c). It is a boom: a pivot at the character's shoulder, an
 * arm of `input.camDist` metres swung by `input.camYaw`/`input.camPitch`, and a spring on
 * every channel so nothing in the world can make the picture jump.
 *
 * Everything here is client-only. `input.bodyYaw` is the one number that goes on the wire.
 */
/** Pivot height above the feet, as a fraction of the standing eye height. Shoulder, not eye. */
const CAM_PIVOT_FRAC = 0.92;
/** Sideways offset of the pivot, in metres — the character sits left of centre, WoW-style. */
const CAM_SHOULDER_M = 0.35;
/** Spring time constants (see `springStep`): the pivot chases the body, the arm chases the
 * wheel, and the pull-in chases whatever the occlusion trace found. */
const CAM_PIVOT_SMOOTH_SEC = 0.08;
const CAM_DIST_SMOOTH_SEC = 0.14;
/** A pull-in has to be instant (a wall arriving) but a push-out has to be gentle (the wall
 * leaving), or the camera pops the moment you clear a doorway. */
const CAM_PULLIN_SMOOTH_SEC = 0.02;
const CAM_PUSHOUT_SMOOTH_SEC = 0.22;
/** How far in front of the wall the boom stops, so the near plane never eats geometry. */
const CAM_WALL_PAD_M = 0.25;
/** Camera eye-height spring: how quickly the pivot settles on a stance change. */
const CAM_EYE_SMOOTH_SEC = 0.12;
/**
 * The watchdog. If the resolved boom has not moved more than a hair in this long while the
 * body *has*, the spring is wedged (a NaN from a degenerate trace, a target it can never
 * reach) and the camera is snapped onto its target rather than left behind the player's head.
 */
const CAM_WEDGE_MS = 900;
const CAM_WEDGE_EPS = 0.002;
/**
 * How far a click may reach. The shared acquire range is measured from the *body*; this ray
 * starts at the camera, which the boom puts up to `CAM_DIST_MAX` behind it, so the two only
 * agree once the boom is added. Without this a click would refuse bodies Tab happily takes.
 */
const PICK_RANGE = TARGET_ACQUIRE_RANGE + CAM_DIST_MAX;
/** Hover picks run on a clock, not on every mousemove: 20 Hz is under the eye's notice. */
const HOVER_INTERVAL_MS = 50;

/** Reach for containers and loot bags (M2-DESIGN §3 says 2 m; 2.5 is forgiving). */
const INTERACT_RANGE = 2.5;
/** How often the interact scan runs. Ten times a second is well under human reaction. */
const INTERACT_SCAN_MS = 100;
/**
 * The ring drawn under somebody a finished quest rung is waiting to be handed in to.
 *
 * Deliberately wider than `NPC_TALK_RANGE`. Every other ring in the game is a place to *stand* —
 * HOLD and REACH are positional and resolve on arrival — so a ring drawn at exactly the talk
 * radius would promise the same thing and then do nothing, because a hand-in resolves on E and
 * not on presence. At 4.5 m the ring is an approach: you cross it, the "Speak" prompt comes up a
 * metre and a half later as you close, and the ring never sits where the prompt does not.
 */
const HAND_IN_RING_RADIUS = NPC_TALK_RANGE + 1.5;

/** Handed to the HUD when there are no extracts at all (leaving a raid). */
const NO_EXTRACTS: readonly ExtractSnap[] = [];
/** The camera's occlusion ray traces the map and nothing else — see the boom. */
const NO_HIT_TARGETS: readonly HitTarget[] = [];

/**
 * What the crowd renderer needs to draw the local player, assembled once a frame from the
 * predictor and the input (plan §1c). The local body is an instance like every other body in
 * the world; nothing here is a special case except that its position is predicted rather than
 * interpolated from a snapshot.
 */
export interface LocalBodyState {
  /** Feet, in world metres — the predicted render position, not the last snapshot. */
  feet: Vec3;
  /** Where the body faces. The camera's own yaw is deliberately not here. */
  bodyYaw: number;
  /** A shared `AnimState`, the same u8 `PlayerSnap.anim` carries for everybody else. */
  anim: number;
  /** Clip-time multiplier, so one walk cycle serves a stroll and a sprint. */
  animSpeed: number;
  /** Horizontal speed in m/s, for anything that wants the raw number. */
  speed: number;
  /** Where the player is looking, for a head/spine aim if the rig ever wants one. */
  look: { yaw: number; pitch: number };
}

/** Keeps every extract's ambient hum in step with whatever `beacons.setExtracts()` was
 * just told — cheap even called every raidState, see audio/README.md §7. */
function syncBeaconAudio(list: readonly ExtractSnap[]): void {
  for (const x of list) sound.beacon(x.id, x.pos, x.active);
}

/** What the player is standing next to, if anything. */
interface InteractCandidate {
  /** Container ids come from the map file; bag ids are entity ids (>= 40000); NPC ids
   *  come from the map file too and are only ever handed straight back to the server. */
  kind: 'container' | 'bag' | 'npc';
  id: number;
  label: string;
  pos: Vec3;
}

/** Wander this far from the container/bag the loot panel was opened from and it closes
 * itself — "loot box locks me in place" was the #1 playtest complaint. */
const LOOT_FOLLOW_RANGE = 3;

// ------------------------------------------------------------------ M3 tuning
/** Eye height while downed: on the floor, but still able to see who is coming. */
const DOWNED_EYE_HEIGHT = 0.4;
/** Breach shoulder-charge: how far the camera lunges forward, and for how long. */
const BREACH_LUNGE_M = 0.55;
const BREACH_LUNGE_SEC = 0.35;
/**
 * The Verger's hazard panels arrive as `Ev.Ability` with playerId 0. The boss has no
 * Calling, so the slot field doubles as an effect id — 7 is the electrified floor.
 * (Agreed with the server agent; documented in the M3 client report.)
 */
const BOSS_EFFECT_HAZARD = 7;
/** Mark tier → toast tone (see overlay.tsx `ToastTone`). */
const MARK_TONES = ['bronze', 'bronze', 'amber', 'hymn'] as const;

/**
 * One step of an exact critically-damped spring (Ryan Juckett's damped simple harmonic
 * motion, the ω0 === ω branch). `smoothTimeSec` behaves like an exponential time
 * constant — `omega = 2 / smoothTimeSec` is the usual SmoothDamp-style approximation.
 * Used for the camera eye height so a stance change reads as a physical settle rather
 * than a snap or an under-damped bounce.
 */
function springStep(
  x: number,
  v: number,
  target: number,
  dt: number,
  smoothTimeSec: number,
): [number, number] {
  const omega = 2 / Math.max(0.001, smoothTimeSec);
  const x0 = x - target;
  const c2 = v + omega * x0;
  const et = Math.exp(-omega * dt);
  const newX0 = (x0 + c2 * dt) * et;
  const newV = (c2 - omega * (x0 + c2 * dt)) * et;
  return [target + newX0, newV];
}

/**
 * v3's default ground is the city, not the tutorial yard (§7.23). An unknown id falls back to
 * Lanternfall rather than to the dev pad: a player handed a stale link lands somewhere the game
 * is actually played.
 */
const DEFAULT_MAP: MapDef = MAPS[DEFAULT_MAP_ID] ?? YARD;

function pickMap(id: string): MapDef {
  return MAPS[id] ?? DEFAULT_MAP;
}

function spawnOf(map: MapDef): Vec3 {
  return map.spawns[0] ?? { x: 0, y: map.world.groundY, z: 0 };
}

/** Slop for matching feet height against a box's top face. */
const SURFACE_EPS = 0.15;

/**
 * Footstep surface for the audio layer: the shared World/MapDef carries no per-area
 * material tag (see audio/README.md §2), so this approximates it from geometry —
 * standing on any box top (a deck, a platform, a crate) reads as `'metal'`; the raw
 * ground/terrain (dunes, the map's own apron) reads as `'regolith'`.
 */
function surfaceAt(pos: Vec3, world: World): 'regolith' | 'metal' {
  for (const b of world.boxes) {
    if (b.min.y < 0) continue; // below-grade geometry (the ground apron) never counts
    if (pos.x < b.min.x || pos.x > b.max.x || pos.z < b.min.z || pos.z > b.max.z) continue;
    if (Math.abs(pos.y - b.max.y) < SURFACE_EPS) return 'metal';
  }
  return 'regolith';
}

/**
 * Every map's Blender terrain sidecar (see packages/shared/src/maps/region.ts): re-authors the
 * shared `MapDef` against the sculpted heightfield + colliders so prediction here walks the exact
 * ground the server does (apps/server/src/index.ts reads the same files from disk). Must settle
 * before the first deploy; a 404 or a rejected file leaves that map's in-code terrain standing,
 * which is only correct if the server has none either.
 */
const REGION_SIDECARS: readonly { mapId: string; apply: (r: RegionSidecar | null) => MapDef }[] = [
  { mapId: 'spine', apply: applySpineRegion },
  { mapId: 'lanternfall', apply: applyLanternfallRegion },
  { mapId: 'vigil-downs', apply: applyDownsRegion },
];

async function loadRegionSidecars(): Promise<void> {
  await Promise.all(
    REGION_SIDECARS.map(async ({ mapId, apply }) => {
      const url = `${regionAssetBase(mapId)}.json`;
      try {
        const res = await fetch(url, { cache: 'no-cache' });
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const region = parseRegion(await res.json(), REGION_SPECS[mapId]!);
        const map = apply(region);
        // The visual-only lists (lanterns, benches, the grass mask) go to the render layer
        // rather than onto a MapDef the server also holds. See render/region.ts.
        setRegionSidecar(mapId, region);
        console.info(
          `[stillsun] region applied to ${mapId} (${region.boxes.length} colliders, ${map.world.boxes.length} boxes)`,
        );
      } catch (err) {
        console.warn(
          `[stillsun] region sidecar unavailable (${url}); ${mapId}'s in-code terrain stands`,
          err,
        );
      }
    }),
  );
}

async function boot(): Promise<void> {
  const hud = new Hud();
  // The splash is already on screen (static markup); this takes over its bar and tips.
  const splash = new Splash();
  splash.stage('renderer');
  hud.setBoot('initialising renderer…');
  // Decodes against a throwaway OfflineAudioContext — safe pre-gesture, and cheap enough
  // to run in parallel with the renderer/scene bring-up below rather than block on it.
  void sound.load(SOUND_MANIFEST);
  // The Spine's region: the sidecar (sim) must land before a deploy; the GLB (visuals) is only
  // prefetched so `setMap` can attach it without a mid-raid shader compile.
  const regionReady = loadRegionSidecars();
  prefetchRegions();
  // The hub's generated score; starts once audio is unlocked and the hub is on screen.
  const requiem = new Requiem(sound);

  let gfx;
  try {
    gfx = await createRenderer(hud.canvas);
  } catch (err) {
    console.error('[stillsun] renderer init failed', err);
    splash.fail('graphics init failed — this browser needs WebGPU or WebGL2');
    hud.setBoot('graphics init failed — this browser needs WebGPU or WebGL2.');
    hud.enterButton.disabled = true;
    return;
  }
  const { renderer, camera } = gfx;
  await regionReady;

  let map = DEFAULT_MAP;
  splash.stage('scene');
  const sceneHandle = createScene(renderer, map, gfx.tier);
  const { scene } = sceneHandle;

  // The crowd. Every character in the world — remote players, monsters and, once the third-person
  // camera lands, the local player too — is an instance of one of these meshes, skinned on the
  // GPU from a baked animation texture. Built before the precompile below so its pipelines are
  // warmed with the rest of the scene rather than compiling on screen (WEBGPU-NOTES §1), and
  // awaited: a crowd that arrives after the first frame is a freeze, not a pop-in.
  let crowd: CrowdField | null = null;
  /**
   * `?crowd=N`: N dummy citizens around the player, for the crowd gate in `docs/PERF-BUDGET.md`.
   * Seeded on the first frame after Welcome, when there is a real position to scatter them
   * around, and re-seeded on a map change — a gate whose citizens are still standing in the
   * world the probe deployed *out* of measures an empty frustum and calls it a pass.
   */
  const crowdDebugCount = Math.max(0, Number(queryParam('crowd') ?? 0) | 0);
  /**
   * How wide to scatter them, metres. The budget's row is "300 *visible*", and at the default
   * spread the frustum culls two thirds of them before they cost anything — which measures the
   * culling, not the crowd. `?crowdr=11` puts nearly all of them inside a 70-degree frustum.
   */
  const crowdDebugRadius = Math.max(2, Number(queryParam('crowdr') ?? 26));
  /** Which map the dummies were last placed in, and the slots they hold. */
  let crowdDebugMap = '';
  let crowdDebugSlots: number[] = [];
  try {
    crowd = await CrowdField.create(scene, hud.markerRoot, { bodies: CROWD_BODIES });
  } catch (err) {
    console.warn('[stillsun] crowd bodies unavailable; characters will not draw', err);
  }

  const net = new NetClient();
  // Remote players, monsters and the people standing on the map all draw through the crowd: none
  // of them owns an `Object3D` any more. They take it `null`-tolerantly, so a build whose bodies
  // failed to load still simulates, still targets and still talks — it just has nobody in it.
  const remotes = new RemoteManager(crowd);
  const entities = new EntityManager(scene, crowd);
  const lootBeams = new LootBeams(scene);
  const containers = new ContainerField(scene);
  const beacons = new ExtractBeacons(scene, hud.markerRoot);
  // Floating damage and heal numbers, pooled into the same projection layer the beacons and
  // the nameplates use. Fed by Ev.Damage / Ev.Heal; no per-frame cost at all.
  const floaters = new FloatingNumbers(hud.markerRoot);
  // M5 Expedition quests: a HUD panel that builds its own DOM in #hud, and a ground ring at the
  // current quest area (ExtractBeacons cannot carry an extra marker — see render/questmarker.ts).
  const quests = new QuestPanel(document.getElementById('hud') ?? document.body);
  /** Quest whose brief has already been spoken, so a heartbeat never repeats it. */
  let lastBriefedQuestId = '';
  /** VoiceId -> the person's name, for the chat prefix. Unknown ids fall back to a system line. */
  const speakerName = (voice: string): string | null => VOICES[voice as VoiceId]?.name ?? null;
  const questMarker = new QuestMarker(scene, hud.markerRoot);
  // The people standing on the map (`MapDef.npcs`) — drawn client-side from map data, never
  // from a snapshot. Her bark goes through the same chat path her quest briefs already use.
  const npcs = new NpcField(scene, crowd, hud.markerRoot, {
    onBark: (speaker, line) => {
      hud.addChat(speaker, line);
    },
  });
  /**
   * The party column, and the little menu that runs the group.
   *
   * It holds no party state of its own: it draws the last `S2C.PartyState` the server sent and
   * every action it offers is a slash command down the ordinary chat path, because those are
   * the only things `game/party.ts` accepts. Clicking a frame selects that member, which is
   * how a Warden heals somebody standing behind them.
   */
  const partyPanel = new PartyPanel({
    onSelect: (id) => {
      setTarget(id);
    },
    onCommand: (text) => {
      net.sendChat(text);
    },
  });
  const effects = new AbilityEffects(scene);
  // Persistent environment damage (bullet holes, sand pocks, scorch). Built before the
  // precompile below so its three pipelines are warmed with the rest of the scene.
  const decals = new DecalField(scene);
  const abilities = new AbilityTracker();
  // The zone map (M). It builds its own DOM in <body> and pulls the two things that move every
  // frame — the squad and the Choir — straight off the managers that already hold them, so
  // nothing here has to be mirrored into a second copy.
  const zoneMap = new ZoneMap({
    viewer: () =>
      welcomed && !hub.visible ? { pos: predictor.state.pos, yaw: input.bodyYaw } : null,
    allies: (out) => remotes.compassTargets(out),
    allyName: (id) => nameFor(id),
    hymns: (out) => entities.revealPoints(out),
    onToggle: (open) => {
      raidBar.setMenuOn('map', open);
      applyUiOpen();
    },
  });
  // The WoW bar and the kill feed. Both close over `input`, `overlay`, `hub`, `selfId` and
  // `nameFor` below: every hook here runs on a click, a key or an event, never at boot, so
  // the later declarations are always initialised by the time one fires.
  const raidBar = new RaidBar({
    onQuickSlot: (index) => {
      useQuickSlot(index);
    },
    onAbilitySlot: (slot) => {
      // Clicking a cell is the key press: `input.pressAbility` latches the same button bit
      // and calls the same `onAbility` hook, so there is one path to a cast and not two.
      if (!input.active || dead || isDowned()) return;
      input.pressAbility(slot);
    },
    onBag: () => {
      if (input.active) overlay.toggleInventory();
    },
    onScores: () => {
      scoresPinned = !scoresPinned;
      hud.setScoreboardVisible(scoresPinned);
      raidBar.setMenuOn('scores', scoresPinned);
    },
    onChat: () => {
      if (!hud.chatOpen) {
        input.chatMode = true;
        hud.openChat();
      }
    },
    onFeedback: () => {
      overlay.openFeedback('');
    },
    onSettings: () => {
      overlay.toggleSettings();
    },
    onMap: () => {
      if (input.active && !dead) zoneMap.toggle();
    },
    isActive: () => input.active && !hub.visible && !hud.chatOpen,
  });
  const killFeed = new KillFeed(hud.killFeedRoot, {
    selfId: () => selfId,
    nameOf: (id) => nameFor(id),
    isEntity: (id) => isEntityId(id),
  });
  let predictor = new Predictor(0, spawnOf(map));
  /**
   * Where the *next* Welcome should put the body, when something already knows better than the
   * map's own first spawn point. A zone hand-off is the only thing that sets it: `S2C.Transfer`
   * carries the authoritative arrival, and the `Welcome` for the new room lands a tick later and
   * would otherwise build a fresh `Predictor` at the target map's default spawn — which is the
   * middle of the Downs, not the gate the player just walked through. Consumed exactly once.
   */
  let pendingSpawn: { pos: Vec3; yaw: number } | null = null;

  // Pixel reads only ever happen inside the frame loop; see render/capture.ts.
  const capture = new FrameCapture(hud.canvas);
  const collector = new FeedbackCollector();
  collector.live.backend = gfx.backend;
  collector.live.quality = gfx.tier;
  collector.live.mapId = map.id;

  /** Client-local: the protocol carries no mode, so the client decides what a death means. */
  let mode: HubDeployRequest['mode'] = 'expedition';
  /** Set while the debug flag is on, so the raid furniture is visible without the Spine map. */
  const debugRaid = new URLSearchParams(window.location.search).get('debug') === 'raid';

  // The camera is parented into the scene so anything that wants to hang off it can; it is
  // written every frame by the boom below.
  scene.add(camera);

  // Build every render pipeline the start scene needs while the loading screen is still up.
  // Skipped and the first `render()` compiles them synchronously on the main thread instead,
  // which is what froze the tab on load — see `precompileScene` in render/renderer.ts.
  hud.setBoot('compiling shaders…');
  splash.stage('shaders');
  // The three skinned Hymn bodies are parked in the scene first, so the compile below warms
  // their nine pipelines here instead of stalling the main thread for four seconds the first
  // time a Chorister walks into view. Fail-soft: a missing GLB just leaves them cold.
  await entities.warmRigs();
  await npcs.warmBodies();

  // --------------------------------------------------------------- post chain ---
  // The world is rendered through this instead of `renderer.render(scene, camera)`; see the
  // header of `render/post.ts`. `?post=0` makes it a pass-through.
  // Its `compile()` has to run here too: the scene pass draws into its own MRT render target,
  // which is a different pipeline key from the canvas `precompileScene` just warmed.
  // No overlay argument any more: the first-person view model went with the pivot (plan §1c),
  // so there is no second scene drawn on top of the world.
  const post = createPost(renderer, scene, camera, gfx.tier);
  // Screen-space light shafts need the Still's direction and transmitted colour (render/post.ts).
  post.setSunDirection(sceneHandle.sunDir, sceneHandle.sun.color);
  // Late-arriving content (container GLBs, Hymn rigs, loot beams) asks for a coalesced
  // precompile through this hook instead of compiling on its first draw — render/renderer.ts.
  setLatePrecompileHook((label) => {
    void precompileScene(renderer, scene, camera, `late:${label}`, () => post.compileScene());
  });
  // The neighbouring map's pipelines, before the compile rather than after it. A gate crossing
  // is an `S2C.Transfer` and a `setMap` with no loading screen anywhere in it, so whatever the
  // other persistent map needs and this one does not — its terrain, grass and life layer, its
  // region GLB's prop kits, a greybox class this map's boxes never populate — used to compile
  // mid-stride. This builds the whole of it, the way `setMap` would, into a warm pool and
  // registers it. Awaited (the neighbour's region GLB is a fetch) and above the precompile, not
  // below it: `registerWarmPool` only appends to a list the *next* `runPrecompile` reads. See the
  // block over `buildWarmPool` in render/scene.ts.
  await sceneHandle.warmNeighbours();
  // Behind the loading screen, with the warm-up pools attached and the loop parked: the
  // chain's own `compile()` (a real render for the effect quads and the recorded call depths,
  // then the async walk for everything the camera cannot see), or — with `?post=0` — the
  // canvas compile plus the disabled handle's view-model compile (render/post.ts).
  await precompileScene(renderer, scene, camera, map.id, () => post.compile());
  splash.stage('post');
  // The easy way is still worth writing down, because it is the one somebody will reach for
  // again: swapping the other map in behind the loading plate and calling `precompileScene`
  // compiles almost nothing. `compileAsync` culls like a render (PERF-BUDGET rule 5) — its
  // `_projectObject` tests `!object.frustumCulled || frustum.intersectsObject(object)` — and the
  // second world's batches sit two hundred metres from the boot camera, so the walk finds nothing
  // to compile. Measured that way: a gate crossing still cost 3 new pipelines and a ~124 ms frame
  // against 4 and ~158 ms with no warm at all. The pool above opts every mesh out of that test
  // instead, which is the entire difference; its position is then irrelevant and its 0.001 scale
  // is only so `post.compile()`'s one real `pipeline.render()` draws a speck.
  // One warm covers the round trip: the boot map's own pipelines are warm by construction, so
  // lanternfall <-> vigil-downs is closed by warming whichever of the two we did not boot on. It
  // is boot-only and reads the boot map's `exits`, so a third gate off the *neighbour* would not
  // be covered — that wants a `warmNeighbours()` after the Transfer `setMap` down in `onTransfer`.
  // A region GLB that lands *after* a Spine `setMap` (a slow first fetch) attaches late; compile
  // its six materials with the loop parked rather than on the first frame that draws them.
  sceneHandle.onRegionAttached = () => {
    void precompileScene(renderer, scene, camera, 'spine-region', () => post.compileScene());
  };

  hud.setBoot(gfx.backend === 'webgpu' ? 'webGPU - enabled' : 'WebGL2 - enabled');
  splash.done();

  // ---------------------------------------------------------------- input ---
  const input = new InputController({
    canvas: hud.canvas,
    onChatOpen: () => {
      input.chatMode = true;
      hud.openChat();
    },
    onEscape: () => {
      // Innermost thing first: chat box, then the target, then a panel. The hub is the home
      // screen, so ESC there only ever closes something on top of it. There is no "release the
      // pointer" rung any more — the cursor was never taken away (plan §1c).
      if (hub.visible) {
        hub.escape();
        return;
      }
      if (hud.chatOpen) {
        hud.closeChat();
        input.chatMode = false;
        return;
      }
      // The party menu is the smallest thing on screen and the most obviously "open"; it goes
      // before the target for the same reason chat does.
      if (partyPanel.menuOpen) {
        partyPanel.closeMenu();
        return;
      }
      // The target goes before the panels: in a fight ESC means "drop what I am on", and
      // reaching for it should never cost the player the bag they had open behind it.
      if (input.targetId !== 0) {
        setTarget(0);
        return;
      }
      // The map covers everything, including the bag, so it is the first thing ESC takes away.
      if (zoneMap.open) {
        zoneMap.close();
        return;
      }
      overlay.escape();
    },
    onCycleTarget: (backwards) => {
      cycleTarget(backwards);
    },
    onPick: (x, y) => {
      pickTarget(x, y);
    },
    onRightPick: (x, y) => {
      talkTo(x, y);
    },
    onHover: (x, y) => {
      hoverAt(x, y);
    },
    onInteract: () => {
      // E doubles as "put this panel away" so a player who opened a container by mistake
      // is never stuck holding a key they cannot find. The map is a panel too: without this
      // branch E would fall through and open whatever locker happens to be in reach behind it.
      if (zoneMap.open) {
        zoneMap.close();
        return;
      }
      if (overlay.panelOpen) {
        overlay.escape();
        return;
      }
      if (!input.active || dead) return;
      // A downed squadmate outranks a locker: the revive channel is a *held* Interact
      // (see the frame loop), so E on an ally must not also pop a container open.
      if (reviveTarget !== null || candidate === null) return;
      if (candidate.kind === 'npc') {
        // Talking is a request, not a result: no panel opens, nothing is predicted. The server
        // decides what it means (in The Waking, that she is the way home) and answers with
        // S2C.Extracted — see room.ts's `handleTalk`.
        net.sendInteract(InteractAction.Talk, candidate.id);
        collector.logEvent(`talk ${String(candidate.id)}`);
        if (isVendorNpc(candidate.id)) overlay.openVendor();
        return;
      }
      net.sendInteract(InteractAction.Open, candidate.id);
      // Remembered so the frame loop can close the panel once the player wanders off —
      // see LOOT_FOLLOW_RANGE below.
      openContainerPos = { ...candidate.pos };
      collector.logEvent(`open ${candidate.kind} ${String(candidate.id)}`);
    },
    onInventory: () => {
      if (!input.active) return;
      overlay.toggleInventory();
    },
    onTakeAll: () => {
      overlay.takeAll();
    },
    onFeedback: () => {
      if (hub.visible) hub.openTerminal();
      else overlay.openFeedback('');
    },
    onAbility: (slot) => {
      // The bit is already latched by input.ts; this only starts the HUD sweep on the
      // press frame. The server's next S2C.Progress overrules it either way, including
      // a refusal (no Calling, on cooldown, not enough stamina) which snaps it back.
      if (!input.active || dead || isDowned()) return;
      if (abilities.press(slot)) collector.logEvent(`ability ${String(slot)}`);
    },
  });

  /**
   * Quick slot 1/2/3. Shared by the number keys (input.ts) and the bar's quick slots, so
   * there is exactly one place in the app that means "use what is in slot n".
   */
  const useQuickSlot = (index: number): void => {
    if (!input.active || dead) return;
    const stack = inventory.find((s) => s.location === ItemLocation.Quick && s.slot === index);
    if (!stack) return;
    sound.play('ui_tick');
    net.sendInteract(InteractAction.Use, 0, stack.uid);
    collector.logEvent(`use ${stack.itemId}`);
  };

  // ------------------------------------------------------------- targeting ---
  /**
   * Point-and-click, or Tab (plan §2a). Aiming in STILLSUN is a *selection*: the cursor is
   * unprojected to a world ray and traced with the shared `traceRay` over `aimTargets()` —
   * the same per-character capsules the rest of the client already builds — so picking a body
   * out of three hundred costs one ray and no `Raycaster` walk of the scene graph.
   *
   * `input.targetId` is the single source of truth; every command carries it, and the server
   * decides whether the pick was legal. Nothing here predicts anything.
   */
  /**
   * One writer for `entities.setHighlight`, because three things now want to glow: a loot bag
   * in reach, whoever the cursor is over, and the selected target. Two writers on a 10 Hz scan
   * and a 20 Hz hover would rub each other out every other frame.
   *
   * Drawing the ring itself is the render agent's; this only calls the hooks that already
   * exist. Remote *players* get the same treatment when the crowd renderer lands one.
   */
  let bagHighlight = 0;
  const refreshHighlight = (): void => {
    const id = bagHighlight !== 0 ? bagHighlight : hoverId !== 0 ? hoverId : input.targetId;
    entities.setHighlight(isEntityId(id) ? id : 0);
  };

  const setTarget = (id: number): void => {
    if (input.targetId === id) return;
    input.targetId = id;
    refreshHighlight();
    // The party column shows which of its rows is the one being healed.
    partyPanel.setTargetId(id);
    if (id !== 0) sound.play('ui_tick');
    collector.logEvent(`target ${String(id)}`);
  };

  /** Cursor (CSS px, viewport-relative) -> a world ray from the camera. */
  const pickScratch = new Vector3();
  const pickRay = { origin: { x: 0, y: 0, z: 0 }, dir: { x: 0, y: 0, z: 1 } };
  const cursorRay = (clientX: number, clientY: number): typeof pickRay | null => {
    // The canvas rect, never `innerWidth/innerHeight`: the renderer runs dynamic resolution
    // and CSS-pixel rendering, so the drawing buffer is not the element (render/renderer.ts).
    const r = hud.canvas.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    const ndcX = ((clientX - r.left) / r.width) * 2 - 1;
    const ndcY = -(((clientY - r.top) / r.height) * 2 - 1);
    if (ndcX < -1 || ndcX > 1 || ndcY < -1 || ndcY > 1) return null;
    pickScratch.set(ndcX, ndcY, 0.5).unproject(camera);
    pickScratch.sub(camera.position);
    const len = pickScratch.length();
    if (len < 1e-6) return null;
    pickScratch.multiplyScalar(1 / len);
    pickRay.origin.x = camera.position.x;
    pickRay.origin.y = camera.position.y;
    pickRay.origin.z = camera.position.z;
    pickRay.dir.x = pickScratch.x;
    pickRay.dir.y = pickScratch.y;
    pickRay.dir.z = pickScratch.z;
    return pickRay;
  };

  /** Whoever is under the cursor, or 0. The world itself stops the ray, so a body behind a
   * wall is never picked. */
  const pickAt = (clientX: number, clientY: number): number => {
    const ray = cursorRay(clientX, clientY);
    if (ray === null) return 0;
    return traceRay(ray.origin, ray.dir, PICK_RANGE, aimTargets(), map.world).targetId;
  };

  const pickTarget = (clientX: number, clientY: number): void => {
    if (!input.active || dead || overlay.panelOpen || zoneMap.open) return;
    // A click on empty world clears: that is how a player lets go of something.
    setTarget(pickAt(clientX, clientY));
  };

  /**
   * Right-click on a person talks to them — the same `InteractAction.Talk` E sends, and the
   * server validates the same `NPC_TALK_RANGE` either way.
   *
   * The person is found by proximity rather than by the ray: `NpcField` builds no hit capsules
   * (they are drawn from map data, never from a snapshot, and nothing else has ever needed to
   * trace one). The cursor still has to be pointing roughly at them — the ray decides *whether*
   * the click was on the world at all — but the pick itself is "the one you are standing next
   * to", which at a talk radius of a few metres is never ambiguous.
   *
   * TODO: when the crowd renderer gives NPCs capsules, trace them here like everything else.
   */
  const talkTo = (clientX: number, clientY: number): void => {
    if (!input.active || dead || overlay.panelOpen || zoneMap.open) return;
    if (cursorRay(clientX, clientY) === null) return;
    // A Waker under the cursor outranks an NPC nearby: right-clicking a person is how you ask
    // them into your group, and the ray is a real hit on their capsule rather than the "whoever
    // I am standing next to" proximity guess the NPC path below has to make. The plate over
    // their head is inside the same capsule, so right-clicking the name works too.
    const picked = pickAt(clientX, clientY);
    if (picked !== 0 && !isEntityId(picked) && picked !== selfId) {
      partyPanel.openForPlayer(picked, nameFor(picked), clientX, clientY);
      return;
    }
    const person = npcs.nearest(feet);
    if (person === null) return;
    net.sendInteract(InteractAction.Talk, person.id);
    collector.logEvent(`talk ${String(person.id)}`);
    if (isVendorNpc(person.id)) overlay.openVendor();
  };

  /**
   * Every body on the current map that keeps a counter, by id. Rebuilt from `MapDef.npcs` each
   * time a map is seeded, so it costs nothing per click and empties itself on a zone change.
   */
  const vendorNpcIds = new Set<number>();

  /**
   * Is this person a counter? A read of `NpcDef.vendor` — the same map-data flag the server’s
   * repair reach test reads — so the panel and the thing that grants it cannot drift apart.
   * Matching her by name would have broken the moment somebody renamed Sabel.
   */
  const isVendorNpc = (id: number): boolean => vendorNpcIds.has(id);

  /**
   * Soft highlight under the cursor. The mousemove only records where the cursor is; the pick
   * itself runs on the frame loop's own clock, so a fast mouse costs no extra rays.
   */
  let hoverAtX = 0;
  let hoverAtY = 0;
  let hoverPending = false;
  /** Who the cursor is over, if it is not already the selected target. */
  let hoverId = 0;
  const hoverAt = (clientX: number, clientY: number): void => {
    hoverAtX = clientX;
    hoverAtY = clientY;
    hoverPending = true;
  };

  /** Tab / Shift-Tab. Nearest first, id as the tie-break, so the server walks the same ring. */
  const targetRing: TargetCandidate[] = [];
  const cycleTarget = (backwards: boolean): void => {
    if (!input.active || dead) return;
    targetRing.length = 0;
    // Hostiles only. Everything with an entity id is the Choir, and the Choir is the only thing
    // in the world that is reliably hostile today — player-versus-player standing needs the
    // faction data Phase 2a adds, and a Tab that cycles onto a squadmate mid-fight is worse
    // than one that skips them. Clicking a friendly still selects them (for heals).
    for (const t of aimTargets()) {
      if (!isEntityId(t.id)) continue;
      const dx = t.pos.x - feet.x;
      const dy = t.pos.y - feet.y;
      const dz = t.pos.z - feet.z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq <= TARGET_ACQUIRE_RANGE_SQ) targetRing.push({ id: t.id, distSq });
    }
    sortTargetCandidates(targetRing);
    setTarget(nextTargetId(targetRing, input.targetId, backwards));
  };

  hud.chatInput.addEventListener('keydown', (ev) => {
    ev.stopPropagation();
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const text = hud.chatInput.value;
      if (text.trim().length > 0) net.sendChat(text);
      hud.closeChat();
      input.chatMode = false;
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      hud.closeChat();
      input.chatMode = false;
    }
  });

  // ------------------------------------------------------------------ net ---
  let tick = 0;
  let welcomed = false;
  let selfId = -1;
  let selfName = 'drifter';

  // Combat state for the local player.
  let health = MAX_HP;
  let dead = false;
  let respawnAt = 0;
  /** Where we died, so the camera can sit there instead of following the corpse. */
  const deathFeet: Vec3 = { x: 0, y: 0, z: 0 };
  /**
   * Where our own loot bag is, for the map's red mark — separate from `deathFeet`, which is a
   * mutated singleton that survives a respawn and would otherwise show last run's grave. Null
   * means there is nothing of ours on the ground; the frame loop clears it once we have walked
   * back to the spot and found the bag already gone.
   */
  let bagPos: Vec3 | null = null;
  const scores = new Map<number, { kills: number; deaths: number }>();
  /** Latest S2C.Inventory. The server owns it; this is only what we draw. */
  let inventory: ItemStack[] = [];
  /** The scoreboard is pinned, by the bar's Scores button or by O — never held. */
  let scoresPinned = false;
  /** Nearest container / loot bag in reach, refreshed by the frame loop. */
  let candidate: InteractCandidate | null = null;
  /** Position of the container/bag the loot panel is currently open for, so the frame
   * loop can auto-close it once the player is more than LOOT_FOLLOW_RANGE away. */
  let openContainerPos: Vec3 | null = null;
  /** True once the death countdown has handed the player back to the hub. */
  let raidOver = false;
  /** The hub request behind the current connection, so an auth kick can retry the same deploy. */
  let lastDeploy: HubDeployRequest | null = null;
  /**
   * True while the current deploy is The Waking. It decides one thing on the way back: whether
   * the hub opens with the "Lanternfall opens" card, which is the only moment in the game where
   * a player is told the town has more rooms than they have seen.
   */
  let tutorialRun = false;
  /**
   * Frames still to be *rendered* before the cinematic hands off to the world. Welcome arriving
   * only means the server has answered; the first frames after it are the first ones drawn from
   * real snapshot data with the player standing on the ground. Counted in frames rather than
   * waited on a timer, because the whole complaint was a wait that lied.
   */
  let deployFramesLeft = 0;
  /** One hand-off per deploy: the frame loop must not start a second cross-fade. */
  let revealing = false;

  /**
   * The deploy cinematic: the painted plate, the Ken Burns move and the typed subtitles that
   * cover the canvas from the moment Deploy is pressed until the player is on the ground.
   */
  const cinematic: CinematicHandle = mountCinematic();

  /** One session renewal per deploy; a second 'auth' kick goes back to the hub. */
  let authRenewed = false;

  // ------------------------------------------------------------------ M3 state
  /** Local player's SnapFlags from the last snapshot — drives every ability grade. */
  let selfFlags = 0;
  /**
   * Local player's packed `PlayerSnap.look` from the last snapshot: race, class and the four
   * visible gear ids. It is the server's word, not a guess assembled from the local inventory —
   * equipping is a server decision, so the body only changes once the server agrees it did.
   */
  let selfLook = 0;
  /** performance.now() the local downed clock expires at; 0 when up. */
  let downedUntil = 0;
  /** The downed squadmate in reach, and how long Interact has been held on them. */
  let reviveTarget: { id: number; name: string } | null = null;
  let reviveHeldSec = 0;
  /** Breach lunge timer (seconds remaining) and the yaw it was cast along. */
  let lungeLeft = 0;
  let lungeYaw = 0;
  /** Scratch reused by the Pulse reveal; never reallocated per frame. */
  const revealScratch: RevealPoint[] = [];
  /**
   * Ids in an ability effect can name an entity, a remote, or *us*. RemoteManager
   * deliberately holds no record of the local player, so without the `selfId` case a
   * Tinker's own hijack leash — the one that matters most — would resolve to null and
   * never draw. `feet` is the predicted local position, refreshed earlier this frame.
   */
  const resolvePosition = (id: number): Vec3 | null => {
    if (isEntityId(id)) return entities.positionOf(id);
    if (id === selfId) return dead ? null : { x: feet.x, y: feet.y, z: feet.z };
    return remotes.positionOf(id);
  };

  // Entity ids share the kill feed and hit markers with players, so a Chorister has to be
  // able to name itself the same way a drifter does.
  const nameFor = (id: number): string =>
    id === selfId ? selfName : isEntityId(id) ? entities.nameOf(id) : remotes.nameOf(id);

  /**
   * A floating damage or heal number over whoever it happened to. Projected once, at the
   * moment of the hit (see ui/floaters.ts) — so a body that is not on screen, or a hit that
   * lands while the hub is up, costs one lookup and nothing else.
   */
  const floatScratch = new Vector3();
  const floatNumber = (id: number, amount: number, kind: FloaterKind): void => {
    if (!input.active || hub.visible || cinematic.covering) return;
    const pos = resolvePosition(id);
    if (!pos) return;
    floaters.add(
      pos,
      amount,
      kind,
      camera,
      floatScratch,
      hud.canvas.clientWidth,
      hud.canvas.clientHeight,
    );
  };

  /** Reused ability-cell view; filled six times a frame and never kept by the bar. */
  const slotView: AbilitySlotView = {
    fraction: 0,
    remainingMs: 0,
    gcdFraction: 0,
    def: undefined,
    ready: false,
    short: false,
    stamina: 0,
  };
  /**
   * Whether auto-attack is on, as far as this client knows.
   *
   * `Buttons.AutoAttack` is a rising-edge toggle the **server** owns; there is no field on the
   * wire that reports its state back. So this is the honest thing available: the client counts
   * the toggles it has itself sent. It is cleared on death and on leaving a raid, where the
   * server's own auto-attack certainly stops, rather than being guessed at from `InCombat` —
   * swinging at something out of range is not combat, and a wrong lamp is worse than none.
   */
  let autoAttackOn = false;
  /** Last frame's AutoAttack bit, so the toggle counts edges and not held frames. */
  let autoBitLast = false;

  /** Reused target-frame view. Same contract as `slotView`: filled, read, forgotten. */
  const targetView: TargetView = {
    name: '',
    level: 0,
    hp: 0,
    maxHp: 1,
    friendly: false,
    castMs: 0,
    castFraction: 0,
    castName: '',
  };

  /**
   * One frame of the target frame and of the target's nameplate health bar.
   *
   * The cast bar has two sources and they never overlap: `EntitySnap.castEndsAt` for a Hymn
   * (the Cantor's wind-up, on the wire only while it is charging) and `Ev.Cast` for a player.
   * The nameplate bar is written through whichever manager owns the body — exactly one of
   * them can hold it, so the second call is only ever a fallback, never a second writer.
   */
  const updateTargetFrame = (id: number, nowMs: number): void => {
    if (id === 0) {
      hud.setTarget(null);
      crowd?.setPlateHp(-1, null);
      return;
    }
    if (isEntityId(id)) {
      const info = entities.targetInfo(id);
      if (!info) {
        hud.setTarget(null);
        crowd?.setPlateHp(-1, null);
        return;
      }
      targetView.name = info.name;
      targetView.level = info.level;
      targetView.hp = info.hp;
      targetView.maxHp = info.maxHp;
      targetView.friendly = false;
      targetView.castMs = info.castMs;
      targetView.castFraction = info.castFraction;
      // A Hymn's wind-up has no ability name on the wire, and inventing one would be a lie
      // about what the party is interrupting.
      targetView.castName = 'Casting';
    } else if (id === selfId) {
      targetView.name = selfName;
      targetView.level = abilities.level;
      targetView.hp = health;
      targetView.maxHp = MAX_HP;
      targetView.friendly = true;
      targetView.castMs = abilities.castMs;
      targetView.castFraction = abilities.castFraction;
      targetView.castName = abilities.castDef?.name ?? 'Casting';
    } else {
      const info = remotes.targetInfo(id, nowMs);
      if (!info) {
        hud.setTarget(null);
        crowd?.setPlateHp(-1, null);
        return;
      }
      targetView.name = info.name;
      targetView.level = info.level;
      targetView.hp = info.hp;
      targetView.maxHp = info.maxHp;
      targetView.friendly = true;
      targetView.castMs = info.castMs;
      targetView.castFraction = info.castFraction;
      targetView.castName =
        info.castMs > 0 ? (abilityBySlot(info.calling, info.castSlot)?.name ?? 'Casting') : '';
    }
    hud.setTarget(targetView);
    if (!entities.setTargetPlate(id) && !remotes.setTargetPlate(id)) crowd?.setPlateHp(-1, null);
  };

  /** Killer id already shown on the death screen; -1 = nothing shown yet. */
  let killerShown = -1;

  /**
   * The kill cam owns the camera (and the death card) for KILLCAM_TOTAL_MS after a death
   * to another player; when it finishes it hands straight back to the normal death
   * screen below. Every exit from being dead — respawn, hub, kick — aborts it.
   */
  const killCam = new KillCam(scene, camera, remotes, hud, () => {
    if (!dead) return;
    hud.showDeath(killerShown > 0 ? nameFor(killerShown) : null);
  });

  /**
   * Downed is *not* death: the server keeps simulating us at prone speed with no weapon,
   * and a squadmate can still put us back on our feet. So it never touches the death
   * path — no killer card, no respawn countdown, and the look stays free (you can watch
   * whoever dropped you walk over).
   */
  const isDowned = (): boolean => (selfFlags & SnapFlags.Downed) !== 0 && !dead;

  /**
   * Enters (or refines) the death state. Two sources report the same death in the same
   * tick — the Snapshot's Dead flag, which knows only *that* we died, and Ev.Death, which
   * names the killer — and the server may write either to the socket first. So this must
   * not early-return once dead: whichever arrives second still has to be able to upgrade
   * a bare "Dropped" into "Dropped by <name>".
   */
  const enterDeath = (killerId: number, feet: Vec3): void => {
    if (!dead) {
      dead = true;
      respawnAt = performance.now() + RESPAWN_DELAY_MS;
      deathFeet.x = feet.x;
      deathFeet.y = feet.y;
      deathFeet.z = feet.z;
      input.lookLocked = true;
      killerShown = -1;
      // The map is the raid HUD, and this is the death screen: put it away, and leave a mark on
      // it where the bag now is. `deathFeet` outlives the raid, so the mark is its own field.
      zoneMap.close();
      zoneMap.setBag(feet);
      bagPos = { x: feet.x, y: feet.y, z: feet.z };
      // The kill cam owns the camera from here; a right button still physically down must not
      // keep spinning a boom nobody is looking through, and a corpse holds no target.
      input.endOrbit();
      setTarget(0);
    }
    const named = killerId !== 0 && killerId !== selfId;
    if (named && killerShown !== killerId) {
      killerShown = killerId;
      hud.showDeath(nameFor(killerId));
    } else if (killerShown === -1) {
      killerShown = 0;
      hud.showDeath(null);
    }
  };

  const leaveDeath = (): void => {
    if (!dead) return;
    killCam.abort();
    // Poses recorded before a respawn belong to a corpse across the map; keeping them
    // would make the *next* kill cam's ghost teleport mid-replay.
    killCam.resetHistory();
    dead = false;
    respawnAt = 0;
    killerShown = -1;
    input.lookLocked = false;
    hud.hideDeath();
    health = MAX_HP;
  };

  // ------------------------------------------------------- hub / raid panels ---

  /**
   * Hands the player back to Lanternfall. The still behind the hub is grabbed from the
   * frame loop first, so the hub opens over the place they just left rather than over a
   * black rectangle.
   */
  const returnToHub = (): void => {
    raidOver = true;
    // The hand-back out of a raid — and out of The Waking — is where a deploy that never
    // finished loading gets cleaned up: an auth kick or a dropped socket can land here with the
    // plate still on screen, and Lanternfall must never open behind a loading bar.
    cinematic.cancel();
    deployFramesLeft = 0;
    revealing = false;
    // Before `remotes.clear()`: the cam holds a hidden-body flag on one of them.
    killCam.abort();
    killCam.resetHistory();
    net.disconnect();
    welcomed = false;
    input.active = false;
    input.lookLocked = false;
    input.endOrbit();
    setTarget(0);
    overlay.hideCard();
    overlay.closeLoot();
    overlay.setInventoryOpen(false);
    overlay.clearRaid();
    overlay.setPrompt('');
    hud.hideDeath();
    hud.setInteractAvailable(false);
    remotes.clear();
    entities.clear();
    lootBeams.clear();
    beacons.clear();
    quests.clear();
    questMarker.clear();
    // Closes the panel and drops the run's own marks (the bag, the Verger's announced ground).
    zoneMap.clear();
    bagPos = null;
    // Cleared with the raid, so her greeting is armed once per deploy rather than once ever.
    npcs.clear();
    hud.setCompassExtracts(NO_EXTRACTS);
    sound.reset();
    effects.clear();
    decals.clear();
    abilities.reset();
    raidBar.clear();
    killFeed.clear();
    floaters.clear();
    // The server's auto-attack certainly stopped when the raid did, so the lamp goes out with
    // it rather than carrying a stale "on" into the next deploy.
    autoAttackOn = false;
    autoBitLast = false;
    scoresPinned = false;
    hud.setDowned(null, DOWNED_DURATION_MS / 1000);
    hud.setRevive(null, 0);
    hud.setBoss(null);
    hud.setTarget(null);
    hud.setAbilityGrade(null);
    selfFlags = 0;
    selfLook = 0;
    downedUntil = 0;
    reviveTarget = null;
    reviveHeldSec = 0;
    lungeLeft = 0;
    candidate = null;
    dead = false;
    // The hub never waits on a pixel read: a capture only settles when the frame loop
    // drains it, and a frame loop that has stopped would strand the player on a blank
    // screen. Show the hub now over its gradient fallback; the still lands when it lands.
    hub.show();
    // Straight out of The Waking: the town gets introduced, once, before anything else.
    if (tutorialRun) {
      tutorialRun = false;
      hub.announceFirstLight();
    }
    requiem.start();
    // Wick, level and the stash all moved while we were out there.
    void session.refresh();
    hub.refresh();
    void capture.request({ maxWidth: 1600, type: 'image/jpeg', quality: 0.6 }).then((url) => {
      hud.setHubBackdrop(url);
    });
  };

  const overlay: OverlayHandle = mountOverlay(hud.overlayRoot, {
    collector,
    capture,
    token: () => session.token,
    screenshotDefault: () => settings.current.screenshotDefault,
    onTake: (uid) => {
      net.sendInteract(InteractAction.Take, overlay.lootContainerId, uid);
      // Optimistic: the protocol has no per-item pickup ack, same tradeoff as elsewhere
      // in this callback (see LOOT_FOLLOW_RANGE) — a Take the server later rejects still
      // gets a sound for nothing.
      sound.play('loot_pickup');
    },
    onTakeAll: () => {
      // T on an emptied box closes it (F8 feedback, 2026-09-04): the second press is the
      // natural "done here" after the first one took everything.
      if (lootItems.length === 0) {
        overlay.closeLoot();
        return;
      }
      // No bulk verb in the protocol: one Take per stack, which the server rate-limits
      // the same way it would a fast player. See the M2 report.
      const id = overlay.lootContainerId;
      for (const stack of lootItems) {
        net.sendInteract(InteractAction.Take, id, stack.uid);
        sound.play('loot_pickup');
      }
    },
    onUse: (uid) => {
      net.sendInteract(InteractAction.Use, 0, uid);
    },
    onDrop: (uid) => {
      net.sendInteract(InteractAction.Drop, 0, uid);
    },
    onCloseLoot: () => {
      lootItems = [];
      openContainerPos = null;
    },
    onReturnToHub: returnToHub,
    onSettingsChanged: () => {
      applySettings();
    },
    onPanelChange: () => {
      applyUiOpen();
    },
    // The overlay binds one key of its own (C, the character sheet); this is the same predicate
    // the raid bar's caps use, so the two can never disagree about when a keypress is the world's.
    isActive: () => input.active && !hub.visible && !hud.chatOpen,
  });

  /**
   * "Is any panel up?" — the union of the overlay's own panels (bag, loot, settings, feedback,
   * the post-raid card) and the zone map, which is not one of them but wants exactly the same
   * treatment. It has to be a union rather than two writers: closing the map while the bag is
   * still open would otherwise re-lock the pointer over an open bag.
   *
   * Movement stays live while a panel is up (see input.ts); only combat is gated.
   */
  const applyUiOpen = (): void => {
    const open = overlay.panelOpen || zoneMap.open;
    input.uiOpen = open;
    input.combatSuppressed = open;
    // Nothing to lock or release: the cursor is always the player's (plan §1c). A panel only
    // stops abilities and the hover highlight; movement and the camera keep working.
    if (open) input.endOrbit();
  };

  /** Mirror of the open container's contents, for Take All. */
  let lootItems: ItemStack[] = [];

  /**
   * Puts the map's own raid furniture in the world. Containers and extract positions are
   * map data and can be shown the moment the map loads; which two extracts are *live* is
   * the server's call and arrives later in S2C.RaidState, so they start cold.
   */
  const seedRaidContent = (m: MapDef): void => {
    // Above the debug branch: the people on a map are the map, not raid furniture, and `?debug`
    // deploys to the yard — where the whole point is being able to look at her.
    // Idempotent for an unchanged set, the same way the lockers and the beacons are: a second
    // Welcome on the same map must not re-download her body or re-arm her greeting.
    npcs.setNpcs(m.npcs);
    // Who keeps a counter here. Cleared first: the last map’s shopkeeper is not on this one.
    vendorNpcIds.clear();
    for (const n of m.npcs ?? []) if (n.vendor === true) vendorNpcIds.add(n.id);
    // The zone map is drawn from the map definition itself (its name, its extracts, the people on
    // it, the deck kit), so it is seeded here with everything else that comes off `MapDef`.
    zoneMap.setMap(m);
    // The zone's ambience floor. Crossfaded, not restarted: Lanternfall and the Vigil Downs are
    // two rooms in one process and a player can cross the gate twice a minute.
    sound.setZoneBed(m.id);
    // The halo's exit readout reads the same names the map does, off the same defs.
    hud.setExtractNames(m.extracts);
    if (debugRaid) {
      containers.setContainers(debugContainers(m.world.groundY));
      const debug = debugExtracts(m.world.groundY);
      beacons.setExtracts(debug);
      hud.setCompassExtracts(debug);
      zoneMap.setExtracts(debug);
      syncBeaconAudio(debug);
      return;
    }
    containers.setContainers(m.containers);
    const defs = m.extracts;
    if (!defs || defs.length === 0) {
      beacons.clear();
      hud.setCompassExtracts(NO_EXTRACTS);
      zoneMap.setExtracts(NO_EXTRACTS);
    } else {
      const list = defs.map((d) => ({
        id: d.id,
        pos: d.pos,
        radius: d.radius,
        active: false,
        progress: 0,
      }));
      beacons.setExtracts(list);
      // None of these are lit yet — which two the server opens arrives in RaidState — so
      // this only ever clears the strip. It still has to run: a second deploy must not
      // leave the last raid's exits on the compass. In The Waking it stays cleared: the way
      // out is a person, not a bearing (see the raidState handler).
      hud.setCompassExtracts(tutorialRun ? NO_EXTRACTS : list);
      // The map shows every extract, lit or cold: the compass answers "which way out now", the
      // map answers "where are the ways out", and a cold one is somewhere to remember.
      zoneMap.setExtracts(list);
      syncBeaconAudio(list);
    }
  };

  // Edge's "Enhance your security on the web" (Enhanced Security Mode) runs sites it deems
  // unfamiliar with the JavaScript compiler off. Measured 2026-09-05 in the owner's Edge: a
  // ten-million-iteration loop at 215 ms against 10 ms with the JIT, and 25-35 ms of main-thread
  // time per frame that the same build ran in 3 ms under a default profile — 19 fps on a 4080.
  // Nothing server-side can opt out; the only fix is the player's exception list, so say so
  // where they look. Two runs, best of, so a cold first pass does not misreport.
  const jitLoopMs = (): number => {
    const t = performance.now();
    let s = 0;
    for (let i = 0; i < 2_000_000; i++) s += (i & 7) * 0.5;
    if (s < 0) console.log(s); // keep the loop observable
    return performance.now() - t;
  };
  const jitMs = Math.min(jitLoopMs(), jitLoopMs());
  const jitOff = jitMs > JIT_OFF_MS;
  collector.live.jit = jitOff ? `off (${jitMs.toFixed(0)} ms)` : `on (${jitMs.toFixed(1)} ms)`;
  if (jitOff) {
    const msg = navigator.userAgent.includes('Edg/')
      ? 'Edge is running STILLSUN without its JavaScript compiler (Enhanced Security Mode). ' +
        'Edge Settings > Privacy, search, and services > Enhance your security on the web > ' +
        'Exceptions > add stillsun.solidux.ai, then reload. Frame rate is a fraction of normal until then.'
      : 'This browser is running STILLSUN without its JavaScript compiler; frame rate is a fraction of normal.';
    overlay.pushToast('JavaScript compiler off · frame rate limited · see chat', 'loot');
    hud.addChat(null, msg);
    collector.logEvent(`jit off: ${jitMs.toFixed(0)} ms probe`);
    console.warn('[stillsun] ' + msg);
  }

  // The renderer's governor stepped the quality tier down because the frame could not be held
  // even at the resolution floor. The new tier applies on reload; say so where the player looks.
  onGovernor((ev) => {
    const name = ev.to === 'low' ? 'Low' : 'High';
    overlay.pushToast(`Graphics set to ${name} to hold the frame rate · reload to apply`, 'loot');
    hud.addChat(
      null,
      `frame time ${ev.emaMs.toFixed(0)} ms at the resolution floor: graphics quality set to ${name}. Reload (or ESC > Settings) to apply.`,
    );
    collector.logEvent(`governor ${ev.from} -> ${ev.to} at ${ev.emaMs.toFixed(1)} ms`);
  });

  const applySettings = (): void => {
    const s = settings.current;
    input.sensitivity = s.sensitivity;
    input.invertPitch = s.invertPitch;
    // The world camera owns the FOV now that there is no view model to negotiate with.
    if (Math.abs(camera.fov - s.fov) > 0.01) {
      camera.fov = s.fov;
      camera.updateProjectionMatrix();
    }
    sound.setMasterVolume(s.masterVolume);
    sound.setSfxVolume(s.sfxVolume);
    sound.setUiVolume(s.uiVolume);
    sound.setAmbienceVolume(s.ambienceVolume);
    sound.setMuted(s.muted);
  };

  /**
   * Leave Lanternfall. Named rather than inlined into the hub host because The Waking's WAKE
   * button is the second caller: it deploys straight into the tutorial room without the hub ever
   * having been on screen, and both routes have to go through exactly the same seeding,
   * precompile and pointer-lock work or one of them ships a stall the other does not.
   */
  const startDeploy = (req: HubDeployRequest): void => {
    {
      sound.play('ui_confirm');
      tutorialRun = req.mode === TUTORIAL_MODE;
      mode = req.mode;
      // Up before anything slow starts. Everything below this line — the map swap, the seeding,
      // and above all `precompileScene`'s 15–20 s of shader work — used to happen behind a frozen
      // still of wherever the camera happened to be parked (Chris: "it's just frozen on some
      // random map for like 30 seconds until the game loads"). The cinematic covers the canvas
      // from this line until the first live frame with the player on the ground, never a timer.
      cinematic.open(req.mapId, hud.lastBackdropUrl);
      deployFramesLeft = 0;
      revealing = false;
      const next = pickMap(req.mapId);
      const mapChanged = next.id !== map.id;
      if (mapChanged) {
        map = next;
        sceneHandle.setMap(map);
      }
      collector.live.mode = req.mode;
      collector.live.mapId = map.id;
      // Seeded *before* the precompile, not after. `seedRaidContent` is what puts the loot
      // lockers and extract beacons into the scene, and `compileAsync` only walks what is
      // already there — seeding afterwards left the Spine's crate materials to compile on the
      // first frame a locker came into view, mid-raid, which is the exact stall the precompile
      // exists to prevent.
      seedRaidContent(map);
      if (mapChanged) {
        // Not awaited: the rest of this handler has to stay inside the click's user-gesture
        // window for pointer lock. `precompileScene` parks the render loop for its duration and
        // restarts it, so the new map's shaders are built off the main thread instead of
        // stalling it on the first frame that touches them. The cinematic's move is a compositor
        // animation precisely so it keeps running while that happens.
        void precompileScene(renderer, scene, camera, map.id, () => post.compileScene());
      }
      raidOver = false;
      hub.hide();
      waking.hide();
      requiem.stop();
      input.active = true;
      input.uiOpen = false;
      void input.enterFullscreen();
      lastDeploy = req;
      authRenewed = false;
      net.connect(session.name, {
        token: session.token,
        mode: req.mode,
        mapId: req.mapId,
        squad: req.squad,
      });
      collector.logEvent(`deploy ${req.mode} ${req.mapId}`);
    }
  };

  const hub: HubHandle = mountHub(hud.hubRoot, {
    collector,
    capture,
    onDeploy: startDeploy,
    onSettingsChanged: applySettings,
    onVisibilityChanged: (visible) => {
      // The hub owns the whole screen; nothing behind it may read input.
      input.uiOpen = visible;
      input.combatSuppressed = visible;
      if (visible) input.endOrbit();
    },
  });

  /**
   * The Waking. Shown instead of Lanternfall for anyone the ledger says has never been out
   * there; WAKE persists the three choices itself and then drops straight into the tutorial
   * room, so a brand-new player's whole path is: callsign, who woke, and the Yard.
   */
  const waking: WakingHandle = mountWaking(hud.wakingRoot, {
    onWake: () => {
      input.uiOpen = false;
      input.combatSuppressed = false;
      startDeploy({ mode: TUTORIAL_MODE, mapId: TUTORIAL_MAP_ID, squad: '' });
    },
  });

  net.on('state', ({ state, detail }) => {
    hud.setConnection(state, detail);
    if (state === 'open') hud.addChat(null, 'link established');
    if (state === 'reconnecting') welcomed = false;
  });

  net.on('welcome', (msg) => {
    welcomed = true;
    // Arm the plate's dismissal. Two frames, not one: the first is drawn from the map swap this
    // Welcome may just have caused, the second is the first honest look at the world.
    // Three frames, not one: the first is drawn from the map swap this Welcome may just have
    // caused, and the player is only genuinely standing on the ground by the third.
    if (cinematic.covering) deployFramesLeft = 3;
    selfId = msg.playerId;
    // The column has to know which row is the viewer's own, and after a gate crossing that id
    // is a different number for the same person.
    partyPanel.setSelfId(selfId);
    tick = msg.tick;
    // A fresh raid/expedition: drop whatever quest the last one left on screen. The server pushes
    // the live one right after this Welcome when there is one.
    quests.clear();
    questMarker.clear();
    const next = pickMap(msg.mapId);
    const mapChanged = next.id !== map.id;
    if (mapChanged) {
      map = next;
      sceneHandle.setMap(map);
    }
    predictor = new Predictor(selfId, pendingSpawn?.pos ?? spawnOf(map));
    if (pendingSpawn) {
      input.bodyYaw = pendingSpawn.yaw;
      input.camYaw = pendingSpawn.yaw;
      pendingSpawn = null;
    }
    // The insertion flyby is gone with the raid loop (plan §1d): the lore plate now cuts
    // straight to the player standing in the world.
    killCam.abort();
    // A fresh raid is not a continuation of the last one — the old poses are another map.
    killCam.resetHistory();
    remotes.setSelf(selfId);
    remotes.setRoster(msg.players);
    entities.clear();
    lootBeams.clear();
    effects.clear();
    decals.clear();
    remotes.clearCorpses();
    abilities.reset();
    selfFlags = 0;
    selfLook = 0;
    downedUntil = 0;
    reviveTarget = null;
    reviveHeldSec = 0;
    lungeLeft = 0;
    scores.clear();
    leaveDeath();
    raidOver = false;
    collector.live.mapId = map.id;
    // Welcome is the authority on which map we are in — the hub's pick is only a request.
    seedRaidContent(map);
    // Only on a real map change. Welcome re-seeds the raid content every time, but
    // `ContainerField.setContainers` is idempotent for an unchanged locker set (same rule as
    // `ExtractBeacons.setExtracts`), so the batches `onDeploy`'s precompile already warmed
    // are kept rather than rebuilt — an unconditional precompile here cost a second ~12 s
    // scene compile at every raid start.
    if (mapChanged)
      void precompileScene(renderer, scene, camera, map.id, () => post.compileScene());
    hud.addChat(null, `entered ${map.name} as #${String(selfId)}`);
  });

  net.on('snapshot', (msg) => {
    tick = msg.tick;
    remotes.onSnapshot(msg.serverTime, msg.players);
    entities.onSnapshot(msg.serverTime, msg.entities);
    lootBeams.onSnapshot(msg.entities);
    if (!welcomed) return;
    for (const p of msg.players) {
      if (p.id !== selfId) continue;
      // Body yaw stays ours: PlayerSnap.yaw is a u8 and reconciling from it twitches the
      // body on every correction (PROTOCOL-V3 §7.2).
      predictor.reconcile(p, msg.lastInputSeq, map.world, input.bodyYaw);
      health = p.hp;
      const wasDowned = (selfFlags & SnapFlags.Downed) !== 0;
      selfFlags = p.flags;
      selfLook = p.look;
      // The flag is the authority on being downed, the same way Dead is: Ev.Downed is
      // the nicer trigger (it starts the clock at the right instant) but a dropped
      // Events packet must not leave us crawling forever, or upright while downed.
      if ((p.flags & SnapFlags.Downed) !== 0) {
        if (!wasDowned) downedUntil = performance.now() + DOWNED_DURATION_MS;
      } else if (wasDowned) {
        downedUntil = 0;
      }
      // A server that sends snapshots but no S2C.Progress still tells us our class — since v3
      // it rides bits 3..5 of `PlayerSnap.look` rather than a `calling` byte of its own (§4).
      // `look` changes mid-session when gear is equipped, so it is read every snapshot.
      const snapCalling = unpackLook(p.look).calling;
      if (!abilities.seen && snapCalling !== abilities.calling) {
        abilities.calling = snapCalling;
        raidBar.setCalling(abilities.defs);
        hud.setCallingName(CALLINGS[abilities.calling]?.name ?? '');
      }
      // The server is the authority on being dead. Ev.Death is the nicer trigger (it
      // names the killer) but a dropped Events packet must not leave us alive forever.
      const flaggedDead = (p.flags & SnapFlags.Dead) !== 0;
      if (flaggedDead && !dead) enterDeath(0, p.pos);
      if (!flaggedDead && dead) leaveDeath();
      break;
    }
  });

  net.on('playerJoined', (msg) => {
    remotes.add(msg.id, msg.name, msg.cosmetics);
    hud.addChat(null, `${msg.name} entered the Band`);
  });

  net.on('playerLeft', (msg) => {
    const name = remotes.nameOf(msg.id);
    remotes.remove(msg.id);
    scores.delete(msg.id);
    hud.addChat(null, `${name} left`);
  });

  net.on('chat', (msg) => {
    // fromId 0 is the room talking, not a Waker (e.g. "the Choir hears you"): print it unattributed,
    // the same way a join/leave notice reads, rather than as a message from player "#0".
    hud.addChat(
      msg.fromId === 0 ? null : msg.fromId === selfId ? 'you' : remotes.nameOf(msg.fromId),
      msg.text,
    );
    // The roaming Verger's arrival is announced on this channel and nowhere else — there is no
    // protocol field for it. The map reads the sentence and hatches the ground it names; every
    // other room line falls straight through (see `roamRegionForNotice` in the shared package).
    if (msg.fromId === 0) zoneMap.noteSystemLine(msg.text);
  });

  net.on('kick', (msg) => {
    welcomed = false;
    killCam.abort();
    remotes.clear();
    hud.addChat(null, `kicked: ${msg.reason}`);
    // An 'auth' kick means the server could not verify our Hello token: stale, minted under
    // a rotated secret, or empty because boot ran while the API was unreachable. Mint a fresh
    // guest and try the same deploy once more; a second refusal sends the player back to the
    // hub with the reason on screen instead of looping.
    if (msg.reason !== 'auth' || lastDeploy === null || authRenewed) return;
    authRenewed = true;
    const req = lastDeploy;
    hud.setConnection('reconnecting', 'renewing session');
    void session.renew(selfName).then((ok) => {
      if (raidOver) return;
      if (!ok) {
        hud.addChat(null, session.current.error || 'could not renew the session');
        collector.logEvent('auth renew failed');
        returnToHub();
        return;
      }
      selfName = session.name;
      collector.logEvent('auth renewed; reconnecting');
      net.connect(session.name, {
        token: session.token,
        mode: req.mode,
        mapId: req.mapId,
        squad: req.squad,
      });
    });
  });

  net.on('events', (msg) => {
    for (const ev of msg.events) {
      switch (ev.kind) {
        case Ev.Damage:
          // Torso flinch on the victim's skinned body (no-op while they are a capsule).
          if (ev.targetId !== selfId) remotes.onDamage(ev.targetId);
          if (ev.attackerId === selfId && ev.targetId !== selfId) {
            // v3 has no headshots: hitscan and its hit locations went with the shooter.
            hud.showHitMarker(false);
            sound.play('hit_marker');
          }
          if (ev.targetId === selfId) {
            health = ev.hpAfter;
            // Scale the vignette with the bite, with a floor so a graze still registers.
            // The amount goes with it: the halo prints it in red by the reticle and wakes.
            hud.flashDamage(clamp(ev.amount / 45, 0.3, 1), ev.amount);
          }
          // The number over the body. Red and larger for what lands on *us*, white for
          // everything else, and only while the raid HUD is up — the hub is not a fight.
          floatNumber(ev.targetId, ev.amount, ev.targetId === selfId ? 'mine' : 'hit');
          break;

        case Ev.Heal:
          // Healing has no flinch and no hit marker: it is a number and a green one.
          if (ev.targetId === selfId) health = ev.hpAfter;
          floatNumber(ev.targetId, ev.amount, 'heal');
          break;

        case Ev.Cast:
          // v3.1 §9.2. Somebody else's cast bar, under the target frame — and `castMs: 0`,
          // which is the interrupt that takes it away again.
          if (ev.casterId !== selfId && !isEntityId(ev.casterId)) {
            remotes.onCast(ev.casterId, ev.slot, ev.castMs, performance.now());
          }
          break;

        case Ev.Death: {
          // No headshots in v3: the feed keeps the flag so the row layout is unchanged.
          const headshot = false;
          const killer = nameFor(ev.killerId);
          const victim = nameFor(ev.victimId);
          // The feed resolves names and kinds itself, from the same callbacks used here.
          killFeed.add({
            killerId: ev.killerId,
            victimId: ev.victimId,
            weaponId: ev.weaponId,
            headshot,
          });
          // Keep a local tally so the scoreboard is populated even before the first
          // S2C.Score arrives (or if the server never sends one).
          const k = scores.get(ev.killerId) ?? { kills: 0, deaths: 0 };
          if (ev.killerId !== 0 && ev.killerId !== ev.victimId) {
            k.kills += 1;
            scores.set(ev.killerId, k);
          }
          const v = scores.get(ev.victimId) ?? { kills: 0, deaths: 0 };
          v.deaths += 1;
          scores.set(ev.victimId, v);

          if (ev.killerId === selfId && ev.victimId !== selfId) {
            collector.logEvent(`killed ${victim}`);
          }
          if (ev.victimId === selfId) {
            collector.logEvent(`killed by ${ev.killerId === 0 ? 'the Band' : killer}`);
            const feet = { x: 0, y: 0, z: 0 };
            predictor.renderPos(feet);
            enterDeath(ev.killerId, feet);
            // Kill cam, but only for a death to another *player*: a Hymn has no
            // first-person view worth replaying, and an entity id resolves to no
            // snapshot history at all. Falls through to the plain death screen if the
            // killer was never in a snapshot we still hold.
            const byPlayer = ev.killerId > 0 && ev.killerId !== selfId && !isEntityId(ev.killerId);
            if (
              byPlayer &&
              killCam.start({
                killerId: ev.killerId,
                killerName: killer,
                victimName: selfName,
                victimFeet: feet,
                weaponId: ev.weaponId,
                headshot,
              })
            ) {
              // The countdown is a client-side beat (the raid "loot lost" panel hangs off
              // it), so push it out rather than let it fire over the replay. The server's
              // own Ev.Respawn is untouched and still aborts the cam if it lands first.
              respawnAt += KILLCAM_TOTAL_MS;
            }
          }
          break;
        }

        case Ev.Respawn:
          if (ev.id === selfId) leaveDeath();
          else remotes.onRespawn(ev.id); // the body they died in stays where it fell
          break;

        case Ev.Ability:
          onAbilityEvent(ev.playerId, ev.slot, ev.pos, ev.targetId);
          break;

        case Ev.Downed:
          if (ev.id === selfId) {
            downedUntil = performance.now() + DOWNED_DURATION_MS;
            selfFlags |= SnapFlags.Downed;
            collector.logEvent('downed');
          } else {
            hud.addChat(null, `${nameFor(ev.id)} is down`, true);
          }
          break;

        case Ev.Revived:
          remotes.onRevived(ev.id);
          if (ev.id === selfId) {
            downedUntil = 0;
            selfFlags &= ~SnapFlags.Downed;
            collector.logEvent('revived');
          }
          if (ev.byId === selfId) collector.logEvent(`revived ${nameFor(ev.id)}`);
          hud.addChat(null, `${nameFor(ev.byId)} brought ${nameFor(ev.id)} back`);
          // Whoever it was, stop channelling: the ally is up.
          reviveHeldSec = 0;
          break;

        case Ev.MarkUnlocked:
          if (ev.playerId === selfId) onMarkUnlocked(ev.markId, ev.tier);
          break;

        case Ev.QuestComplete:
          quests.complete(QUEST_BREATHER_MS);
          // The resolution line is the payoff — it lands before the ledger does.
          if (ev.line.length > 0) hud.addChat(speakerName(ev.voice), ev.line);
          hud.addChat(null, `quest complete · +${String(ev.wick)} wick · +${String(ev.xp)} xp`);
          sound.play('ui_confirm');
          collector.logEvent(`quest ${String(ev.chainIndex + 1)} complete`);
          break;
      }
    }
  });

  // ------------------------------------------------------------ M3: abilities

  /**
   * One `Ev.Ability`. The deployed entities (bulwark, sentry, drone, beacon, fabricator)
   * arrive through the snapshot on their own, so this only adds the things the entity
   * stream cannot carry: the casts that are pure effect.
   */
  const onAbilityEvent = (playerId: number, slot: number, pos: Vec3, targetId: number): void => {
    // The Verger has no Calling: playerId 0 means the boss, and `slot` is an effect id.
    if (playerId === 0) {
      // The hazard panel is the one thing in the game that actually detonates: it leaves
      // a burn on the deck after the panel itself has faded.
      if (slot === BOSS_EFFECT_HAZARD) {
        decals.scorch(pos, map.world);
      } else effects.ring(pos, 6, 0xdfe8ff);
      return;
    }

    const calling = playerId === selfId ? abilities.calling : remotes.callingOf(playerId);
    const def: AbilityDef | undefined = CALLINGS[calling]?.abilities[slot as 0 | 1 | 2];
    const mine = playerId === selfId;

    // Reuses the HUD confirm chime — cheap feedback the local player just cast something,
    // whatever it turns out to be.
    if (mine) sound.play('ui_confirm');

    // An ability from someone whose Calling has not reached us yet still deserves a beat
    // of feedback rather than silence.
    if (!def) {
      effects.ring(pos, 3);
      return;
    }

    switch (def.id) {
      case 'bulwark':
        // The plate itself comes in as an entity; this is the dust it lands in.
        effects.ring(pos, 2.5);
        break;
      case 'breach':
        effects.ring(pos, 2.5);
        if (mine) {
          lungeLeft = BREACH_LUNGE_SEC;
          lungeYaw = input.bodyYaw;
        }
        break;
      case 'pulse':
        // Squad-wide by design: a Shade's ping reveals for everyone who can see it. The
        // Cantor charge clip is reused non-looping as the ring's rising "ping" — heard by
        // whoever is near enough to see the reveal, not just the caster.
        effects.pulse(pos, def.radius);
        sound.cantorCharge(pos, 700);
        break;
      case 'suppress':
      case 'fabricator':
        effects.ring(pos, def.radius);
        break;
      case 'hijack':
        if (targetId !== 0) effects.hijackLeash(playerId, targetId, def.durationMs);
        break;
      case 'veil':
      case 'silence':
      case 'surge':
        // Buffs are read off SnapFlags every frame (Veiled/Silenced/Surging), which is
        // the only source that stays honest when the buff ends early.
        break;
      default:
        effects.ring(pos, Math.max(2, def.radius));
        break;
    }
  };

  /** Mark unlock toast, coloured by tier (bronze / amber / hymn). */
  const onMarkUnlocked = (markId: string, tier: number): void => {
    const def = MARKS.find((m) => m.id === markId);
    const name = def?.name ?? markId;
    const tone = MARK_TONES[clamp(Math.round(tier), 0, 3)] ?? 'bronze';
    const label = tier >= MarkTier.Hymn ? 'Hymn' : tier >= MarkTier.Amber ? 'Lit' : 'Struck';
    overlay.pushToast(`${label} Mark — ${name}`, tone);
    hud.addChat(null, def ? `${name}: ${def.flavour}` : `Mark unlocked: ${markId}`);
    collector.logEvent(`mark ${markId} t${String(tier)}`);
  };

  net.on('progress', (msg) => {
    const change = abilities.onProgress(msg);
    raidBar.setProgress(abilities.xp, abilities.level, change.leveledUpTo !== null);
    if (change.callingChanged) {
      raidBar.setCalling(abilities.defs);
      hud.setCallingName(CALLINGS[abilities.calling]?.name ?? '');
    }
    if (change.leveledUpTo !== null) {
      overlay.pushToast(`Level ${String(change.leveledUpTo)}`, 'level');
      collector.logEvent(`level ${String(change.leveledUpTo)}`);
    }
  });

  net.on('score', (msg) => {
    scores.clear();
    for (const e of msg.entries) scores.set(e.id, { kills: e.kills, deaths: e.deaths });
  });

  // ------------------------------------------------------------- raid messages ---

  net.on('identity', (msg) => {
    // The server resolved the JWT: this is who we actually are, whatever the start
    // modal said. Founder/admin/owner badges in the hub come from the same place.
    selfId = msg.playerId;
    selfName = msg.name;
    session.saveName(msg.name);
    hud.addChat(null, `identified as ${msg.name}${msg.role === 'player' ? '' : ` · ${msg.role}`}`);
  });

  net.on('questState', (msg) => {
    // The two-line brief is speech: say it once, when the quest actually changes, not on every
    // progress tick or 1 Hz heartbeat.
    const briefKey = `${String(msg.chainIndex)}:${msg.questId}`;
    if (msg.brief && briefKey !== lastBriefedQuestId) {
      lastBriefedQuestId = briefKey;
      const who = speakerName(msg.voice ?? '');
      hud.addChat(who, msg.brief[0]);
      hud.addChat(who, msg.brief[1]);
    }
    // A rung the server is holding for a hand-in: the wire carries only the person's id, and the
    // map data this client already drew them from carries the rest. While one is outstanding the
    // marker leaves the quest area and stands on them instead — the area is finished with, and
    // the only thing left to do is walk back to a person.
    const handIn = msg.handInNpcId === undefined ? null : npcs.byId(msg.handInNpcId);
    quests.set(msg, handIn?.name ?? '');
    // Same two things on the map: a mint ring on the ground the rung names, or a lantern on the
    // person it is waiting for. The tracker and the map can never disagree — one call, one truth.
    zoneMap.setQuest(
      msg.area
        ? {
            name: msg.area.name,
            x: msg.area.x,
            y: msg.area.y,
            z: msg.area.z,
            radius: msg.area.radius,
          }
        : null,
      handIn ? { name: handIn.name, x: handIn.pos.x, y: handIn.pos.y, z: handIn.pos.z } : null,
    );
    questMarker.set(
      handIn
        ? { name: handIn.name, pos: handIn.pos, radius: HAND_IN_RING_RADIUS }
        : msg.area
          ? {
              name: msg.area.name,
              pos: { x: msg.area.x, y: msg.area.y, z: msg.area.z },
              radius: msg.area.radius,
            }
          : null,
    );
  });

  net.on('loot', (msg) => {
    // The server re-sends the loot view after every Take. If the panel has already been
    // closed (walked off past LOOT_FOLLOW_RANGE, Escape, T on empty), a late re-send must
    // not pop it back open with no anchor: that is how a player once looted a corpse from
    // half-way across the map (2026-09-04 owner report). Only a fresh Open sets the anchor.
    if (openContainerPos === null) {
      lootItems = [];
      return;
    }
    lootItems = [...msg.items];
    overlay.showLoot(msg.containerId, msg.items);
    // A container the server has answered for is spent as far as the world is concerned.
    if (!isEntityId(msg.containerId)) containers.markOpened(msg.containerId);
  });

  net.on('inventory', (msg) => {
    inventory = [...msg.items];
    overlay.setInventory(inventory);
  });

  /**
   * Seamless zone travel (PROTOCOL-V3 §3.10, §7.21). The city and the zone live in one server
   * process, so this is a hand-off rather than a reconnect: swap the active `MapDef`, teleport
   * the predictor (no interpolation — the correction would be a hundred metres of glide), throw
   * away every remote body and entity because their ids belong to the room we just left, and
   * drop the target for the same reason. The socket stays open and a `Welcome` for the new room
   * arrives immediately behind this, which is what re-seeds the roster and the world.
   */
  net.on('transfer', (msg) => {
    const to = MAPS[msg.mapId];
    if (!to) {
      console.warn(`[stillsun] transfer to an unregistered map "${msg.mapId}"`);
      return;
    }
    collector.logEvent(`transfer ${map.id} -> ${msg.mapId}`);
    if (to.id !== map.id) {
      map = to;
      sceneHandle.setMap(map);
      seedRaidContent(map);
    }
    collector.live.mapId = map.id;
    // A teleport, not a correction: smoothing a hundred metres of hand-off would glide the
    // player back through the gate they just walked out of. Held for the `Welcome` that lands
    // immediately behind this, which rebuilds the predictor and would otherwise drop the body
    // on the target map's default spawn.
    pendingSpawn = { pos: { ...msg.spawn }, yaw: msg.yaw };
    predictor.reset(selfId, msg.spawn);
    input.bodyYaw = msg.yaw;
    input.camYaw = msg.yaw;
    // Every id in flight belongs to the room we just left.
    remotes.clear();
    entities.clear();
    lootBeams.clear();
    effects.clear();
    setTarget(0);
    hoverId = 0;
    // Parties are room-scoped on the server (apps/server/src/game/party.ts), so the group does
    // not come through the gate. The server says so in as many words on the way out; the column
    // empties here rather than showing five rows of ids that now belong to somebody else.
    partyPanel.clear();
    // No load screen and no precompile: both zones are prefetched at boot and share the scene's
    // materials, so the hand-off is a swap and not a build (PROTOCOL-V3 §3.10).
    hud.addChat(null, `crossed into ${to.name}`);
  });

  net.on('partyState', (msg) => {
    partyPanel.setState(msg);
  });

  const scoreRows = (): ScoreRow[] => {
    const rows: ScoreRow[] = [];
    const ids = new Set<number>(scores.keys());
    if (selfId >= 0) ids.add(selfId);
    for (const id of ids) {
      const s = scores.get(id) ?? { kills: 0, deaths: 0 };
      rows.push({ id, name: nameFor(id), kills: s.kills, deaths: s.deaths });
    }
    return rows;
  };

  // ------------------------------------------------------------ start flow ---
  hud.nameInput.value = session.loadName() || hud.loadName();
  applySettings();

  /**
   * Boot flow: callsign → guest account → The Waking, or Lanternfall. Authentication is
   * best-effort — if `/api/auth/*` is not deployed the session drops to 'offline' and the hub
   * still opens, because a player who cannot log in must still be able to play.
   *
   * The fork is deliberately one-sided. The Waking is only shown when the ledger *positively
   * says* this account has never been out there; an unreachable ledger, or one that throws,
   * lands on the hub. Getting it wrong in that direction costs a returning player one extra
   * screen; getting it wrong the other way marches them back through a tutorial.
   */
  const enterLanternfall = async (name: string): Promise<void> => {
    hud.enterButton.disabled = true;
    hud.setBoot('signing in…');
    const state = await session.boot(name);
    selfName = state.player?.name ?? name;
    session.saveName(selfName);
    hud.saveName(selfName);
    if (state.status === 'offline')
      hud.setBoot('account service offline — playing as a local guest');
    else hud.setBoot(`welcome back, ${selfName}`);
    hud.enterButton.disabled = false;

    let fresh = false;
    if (state.status !== 'offline') {
      await progressStore.load(session.token, true);
      const ledger = progressStore.current.data;
      fresh =
        ledger !== null && needsWaking({ counters: ledger.counters, calling: ledger.calling });
    }

    hud.hideStart();
    // Anything the player wrote while the terminal was unreachable goes now.
    void flushQueuedFeedback(session.token);
    if (fresh) {
      // The Waking owns the whole screen the way the hub does; nothing behind it reads input.
      input.uiOpen = true;
      input.combatSuppressed = true;
      waking.show();
    } else {
      hub.show();
    }
    // The Enter click unlocked audio a moment ago, so the requiem may begin.
    requiem.start();
    void capture.request({ maxWidth: 1600, type: 'image/jpeg', quality: 0.6 }).then((url) => {
      hud.setHubBackdrop(url);
    });
  };

  hud.startForm.addEventListener('submit', (ev) => {
    // preventDefault first: if the audio unlock throws (headless browsers, some kiosk
    // modes), the form must still not submit natively, or the app reboots at "/?" and
    // silently drops every query flag. Then the unlock, which must run synchronously
    // inside this gesture or the browser refuses to start the AudioContext — the only
    // place `unlock()` is called; everything else assumes it ran.
    ev.preventDefault();
    try {
      sound.unlock();
    } catch (err) {
      console.warn('audio unlock failed', err);
    }
    const name = (hud.nameInput.value.trim() || 'drifter').slice(0, 20);
    selfName = name;
    hud.saveName(name);
    session.saveName(name);
    void enterLanternfall(name);
  });

  settings.subscribe(() => {
    applySettings();
  });

  // ----------------------------------------------------------------- loop ---
  const timer = new Timer();
  timer.connect(document);

  const feet = { x: 0, y: 0, z: 0 };
  const camTarget = new Vector3();
  /** Scratch for `sound.setListener()` — orientation from the camera, position from the body. */
  const listenerForward = new Vector3();
  /** Scratch: the grass field wants a `Vector3` and `feet` is the sim's plain `Vec3`. */
  const grassOrigin = new Vector3();
  const listenerUp = new Vector3();
  const listenerPos = new Vector3();
  /**
   * Players and Hymns share the cosmetic hitscan, so our own tracer stops on a Chorister
   * instead of passing through it. Reused every frame — both sources hand back their own
   * scratch arrays, and we only hold the references for the duration of the call.
   */
  const targetScratch: HitTarget[] = [];
  /**
   * The compass strip's Waker buffer. Its own array rather than `targetScratch`: that one
   * holds references into RemoteManager's hitscan scratch, which the cosmetic trace is
   * still reading from further down the frame.
   */
  const compassScratch: CompassTarget[] = [];
  const aimTargets = (): readonly HitTarget[] => {
    targetScratch.length = 0;
    for (const t of remotes.hitTargets()) targetScratch.push(t);
    for (const t of entities.hitTargets()) targetScratch.push(t);
    return targetScratch;
  };
  /**
   * The local player's body, as the crowd renderer wants it. One live object, refilled in
   * place: at 300 characters nothing on this path may allocate.
   */
  const localBody: LocalBodyState = {
    feet: { x: 0, y: 0, z: 0 },
    bodyYaw: 0,
    anim: AnimState.Idle,
    animSpeed: 1,
    speed: 0,
    look: { yaw: 0, pitch: 0 },
  };
  /** Read by the console handle, and by the crowd hand-off below. */
  const localBodyState = (): LocalBodyState => localBody;
  /**
   * The crowd slot the local player is drawn in. Acquired on the first frame after Welcome —
   * `CrowdField.acquire()` needs the bodies loaded, and a slot held across a return to the hub
   * would draw a corpse in Lanternfall.
   */
  let localSlot = -1;
  /** The `look` word already pushed into `localSlot`; -1 forces the next frame to push. */
  let localLookDrawn = -1;
  /**
   * Feeds the crowd renderer. The local body is an instance like every other: the only thing
   * that makes it special is that its position comes from the predictor rather than from an
   * interpolated snapshot, and its facing from `input.bodyYaw` rather than from the wire.
   *
   * Appearance rides the same road as everybody else's: `selfLook` is the `PlayerSnap.look` word
   * the server sent for *us*, so equipping a helm changes the local body the moment the server
   * agrees it did, and never a frame before. The word is compared rather than pushed every frame
   * because `setLook` re-derives the race and the four gear ids behind it.
   */
  const publishLocalBody = (): void => {
    const state = predictor.state;
    localBody.feet.x = feet.x;
    localBody.feet.y = feet.y;
    localBody.feet.z = feet.z;
    localBody.bodyYaw = input.bodyYaw;
    localBody.look.yaw = input.camYaw;
    localBody.look.pitch = input.camPitch;
    const hspeed = Math.hypot(state.vel.x, state.vel.z);
    localBody.speed = hspeed;
    // One walk cycle and one run cycle serve every speed: the clip time is scaled instead of
    // a blend tree being built. Below a stroll it is pinned to 1 so an idle never crawls.
    localBody.animSpeed = hspeed > 0.4 ? clamp(hspeed / WALK_SPEED, 0.5, 2) : 1;
    // A cast outranks standing still but not falling or dying: the flag is the server's, so a
    // cast the server refused or cut short stops animating on the next snapshot rather than
    // being predicted here and then contradicted.
    const casting = (selfFlags & SnapFlags.Casting) !== 0;
    localBody.anim = dead
      ? AnimState.Dead
      : isDowned()
        ? AnimState.Downed
        : !state.grounded
          ? state.vel.y > 0.5
            ? AnimState.Jump
            : AnimState.Fall
          : casting && hspeed < 0.4
            ? AnimState.Cast
            : input.sitting
              ? AnimState.Sit
              : hspeed < 0.4
                ? AnimState.Idle
                : hspeed > WALK_SPEED + 0.5
                  ? AnimState.Run
                  : AnimState.Walk;

    if (crowd === null) return;
    // Held only while we are actually standing in a world: no slot in the hub, none on the
    // loading plate, and the slot is given back the moment either is true again.
    const wanted = input.active && welcomed && !hub.visible;
    if (wanted && localSlot < 0) {
      localSlot = crowd.acquire();
      localLookDrawn = -1; // a fresh slot is bare; push whatever we last heard from the server
    } else if (!wanted && localSlot >= 0) {
      crowd.release(localSlot);
      localSlot = -1;
      localLookDrawn = -1;
    }
    if (localSlot < 0) return;
    if (localLookDrawn !== selfLook) {
      localLookDrawn = selfLook;
      crowd.setLook(localSlot, selfLook);
    }
    crowd.setBroken(localSlot, (selfFlags & SnapFlags.Broken) !== 0);
    crowd.setState(
      localSlot,
      localBody.feet,
      localBody.bodyYaw,
      localBody.anim,
      localBody.animSpeed,
    );
  };

  let eyeHeight = PLAYER_EYE_HEIGHT;
  let eyeHeightVel = 0;
  /**
   * The boom's state. Every channel is a position plus a velocity so `springStep` — an exact
   * critically damped spring, the same maths as litew8's `springTo()` — can settle it. Nothing
   * the world does to the camera is allowed to arrive as a jump.
   */
  const camPivot = new Vector3();
  const camPivotVel = new Vector3();
  let camPivotSeeded = false;
  /** The sprung boom length; `input.camDist` is where the wheel put it. */
  let camDist = 5.5;
  let camDistVel = 0;
  /** The occlusion trace's answer, sprung on its own clock: quick in, slow out. */
  let camWall = CAM_DIST_MAX;
  let camWallVel = 0;
  /** Scratch for the occlusion ray; nothing here is allocated per frame. */
  const camRayOrigin: Vec3 = { x: 0, y: 0, z: 0 };
  const camRayDir: Vec3 = { x: 0, y: 0, z: 1 };
  /** Watchdog: the last resolved eye, and when it last actually moved. */
  const camLast = new Vector3();
  let camMovedAt = 0;
  /** Stride clock for footsteps, now that the weapon's foot plants have gone with the pivot. */
  let bobPhase = 0;
  let prevStride = 0;
  /** Landing-dust edge detection — an airborne->grounded transition with real fall speed. */
  let prevGrounded = true;
  let prevVelY = 0;
  /** Hover highlight clock (see HOVER_INTERVAL_MS). */
  let lastHoverAt = 0;
  let fps = 60;
  let lastHudAt = 0;
  let lastScoreAt = 0;
  let lastInteractAt = 0;
  /** Current cosmetic spread (degrees), read by the HUD to scale the crosshair gap. */
  let spreadDeg = 0;

  const frame = (timeMs: number): void => {
    timer.update(timeMs);
    const dt = Math.min(timer.getDelta(), 0.1);
    if (dt > 0) fps += (1 / dt - fps) * 0.08;

    let speed = 0;
    let stance: number = Stance.Stand;
    let aiming = false;
    let interactHeld = false;
    const downed = isDowned();

    // The kill cam writes the camera itself; the block below must not fight it.
    if (killCam.active) killCam.update(dt);

    if (input.active) {
      // A/D turn the body on a clock rather than an event, so the yaw the command carries is
      // the yaw this frame was drawn at.
      input.updateTurn(dt);
      const sample = input.sampleInput();
      interactHeld = (sample.buttons & Buttons.Interact) !== 0;
      // The auto-attack lamp. The bit is latched for one sample (input.ts pulses it), so its
      // rising edge is exactly one toggle — count them, and never draw the state from anything
      // else: the server owns it and does not report it back.
      const autoBit = (sample.buttons & Buttons.AutoAttack) !== 0;
      if (autoBit && !autoBitLast) autoAttackOn = !autoAttackOn;
      autoBitLast = autoBit;
      // Offline / mid-reconnect we still simulate locally (so the world is not
      // frozen behind "connecting…") but the commands must not be queued for
      // reconciliation — the server will never ack input it never received.
      const live = net.connected && welcomed;
      const cmds = predictor.update(dt, sample, map.world, live);
      if (cmds.length > 0) net.sendInputs(cmds);

      const state = predictor.state;
      predictor.renderPos(feet);
      // Our own pose ring, so a kill cam can put our body back into the killer's view.
      // Throttled inside `recordLocal`; nothing is allocated here.
      // The *body's* yaw and the camera's pitch: the kill cam replays us, and what our body was
      // doing is the body yaw, never wherever the boom happened to be pointing.
      killCam.recordLocal(
        net.serverNow(),
        feet,
        input.bodyYaw,
        input.camPitch,
        state.stance,
        selfFlags,
      );
      // "Loot box locks me in place" — the panel now lets the player walk away, but
      // wandering far enough that the container is no longer meaningfully "open" closes
      // it for them rather than leaving a stale panel up.
      if (openContainerPos !== null && overlay.lootContainerId !== 0) {
        const d = Math.hypot(
          feet.x - openContainerPos.x,
          feet.y - openContainerPos.y,
          feet.z - openContainerPos.z,
        );
        if (d > LOOT_FOLLOW_RANGE) overlay.closeLoot();
      }
      stance = state.stance;
      // Aiming down sights went with the guns (plan §1c); the flag stays wired so the
      // crosshair and spread code compile until Phase 2a rewrites them out.
      aiming = false;
      const airborne = !state.grounded;
      const transitioning = state.stanceTicks > 0;

      // Eye height follows the stance (stand/crouch/prone), critically damped so a stance
      // change reads as a physical settle rather than a snap.
      // Downed overrides the stance entirely: the server does not have to force us prone
      // for the camera to be on the floor, and reading `stance` here would leave a
      // downed player standing tall if it did not.
      const targetEye = downed
        ? DOWNED_EYE_HEIGHT
        : stance === Stance.Crouch
          ? CROUCH_EYE_HEIGHT
          : PLAYER_EYE_HEIGHT;
      [eyeHeight, eyeHeightVel] = springStep(
        eyeHeight,
        eyeHeightVel,
        targetEye,
        dt,
        CAM_EYE_SMOOTH_SEC,
      );

      // Stride clock. The footsteps used to ride the weapon's own foot plants, which went with
      // the view model; the phase is kept here instead and a plant is a half-cycle crossing.
      speed = Math.hypot(state.vel.x, state.vel.z);
      if (state.grounded && speed > 0.4) bobPhase += dt * speed * 1.9;
      else bobPhase = 0;

      // Breach: a short forward shove of the camera along the yaw it was cast on, eased out.
      // The player's position is the server's business — this is felt, not moved. It is written
      // into the scratch copy below and never back into a spring, so it cannot accumulate.
      let lungeX = 0;
      let lungeZ = 0;
      if (lungeLeft > 0) {
        lungeLeft = Math.max(0, lungeLeft - dt);
        const k = Math.sin((lungeLeft / BREACH_LUNGE_SEC) * Math.PI) * BREACH_LUNGE_M;
        lungeX = -Math.sin(lungeYaw) * k;
        lungeZ = -Math.cos(lungeYaw) * k;
      }

      // --- the boom ------------------------------------------------------
      // The kill cam has the camera inside the killer's head; leave it alone until it hands back.
      if (!killCam.active) {
        // 1. The pivot: a shoulder above the feet, offset to the side so the character does not
        //    sit in the dead centre of the picture. Sprung, so a stair riser or a server
        //    correction arrives as a settle — this is what the old step smoothing was for.
        const pivotY = (dead ? deathFeet.y : feet.y) + eyeHeight * CAM_PIVOT_FRAC;
        const baseX = dead ? deathFeet.x : feet.x;
        const baseZ = dead ? deathFeet.z : feet.z;
        // Right of the camera's yaw. Forward is `aimDirection`'s (-sin yaw, ·, -cos yaw), so
        // forward × up is (cos yaw, ·, -sin yaw). Offsetting the *pivot* right puts the
        // character left of centre, which is the over-the-shoulder framing the genre uses.
        const wantX = baseX + Math.cos(input.camYaw) * CAM_SHOULDER_M;
        const wantY = pivotY;
        const wantZ = baseZ - Math.sin(input.camYaw) * CAM_SHOULDER_M;
        if (!camPivotSeeded) {
          camPivot.set(wantX, wantY, wantZ);
          camPivotVel.set(0, 0, 0);
          camPivotSeeded = true;
        } else {
          let v: number;
          [camPivot.x, v] = springStep(camPivot.x, camPivotVel.x, wantX, dt, CAM_PIVOT_SMOOTH_SEC);
          camPivotVel.x = v;
          [camPivot.y, v] = springStep(camPivot.y, camPivotVel.y, wantY, dt, CAM_PIVOT_SMOOTH_SEC);
          camPivotVel.y = v;
          [camPivot.z, v] = springStep(camPivot.z, camPivotVel.z, wantZ, dt, CAM_PIVOT_SMOOTH_SEC);
          camPivotVel.z = v;
        }

        // 2. The arm. `input.camDist` is where the wheel put it; this is where it has got to.
        [camDist, camDistVel] = springStep(
          camDist,
          camDistVel,
          input.camDist,
          dt,
          CAM_DIST_SMOOTH_SEC,
        );

        // 3. Occlusion pull-in: one ray from the pivot back along the boom against the map's own
        //    geometry (the shared `traceRay`, with no targets — bodies must never push the
        //    camera in, or standing in a crowd would shove it into your own head). The spring
        //    is asymmetric: a wall arriving has to be instant, a wall leaving has to be gentle,
        //    or clearing a doorway pops the picture.
        const cp = Math.cos(input.camPitch);
        const boomX = Math.sin(input.camYaw) * cp;
        const boomY = Math.sin(input.camPitch);
        const boomZ = Math.cos(input.camYaw) * cp;
        camRayOrigin.x = camPivot.x;
        camRayOrigin.y = camPivot.y;
        camRayOrigin.z = camPivot.z;
        camRayDir.x = boomX;
        camRayDir.y = boomY;
        camRayDir.z = boomZ;
        const wallHit = traceRay(
          camRayOrigin,
          camRayDir,
          camDist + CAM_WALL_PAD_M,
          NO_HIT_TARGETS,
          map.world,
        );
        const wallWant = clamp(wallHit.dist - CAM_WALL_PAD_M, CAM_DIST_MIN * 0.4, CAM_DIST_MAX);
        [camWall, camWallVel] = springStep(
          camWall,
          camWallVel,
          wallWant,
          dt,
          wallWant < camWall ? CAM_PULLIN_SMOOTH_SEC : CAM_PUSHOUT_SMOOTH_SEC,
        );
        const boom = Math.min(camDist, camWall);

        // 4. The eye, written into a scratch copy no spring ever reads back: the lunge (and any
        //    future shake) rides here, so it can never accumulate into the camera's state.
        camTarget.set(
          camPivot.x + boomX * boom + lungeX,
          camPivot.y + boomY * boom,
          camPivot.z + boomZ * boom + lungeZ,
        );

        // 5. The watchdog. A resolved eye that has not moved while the body has is a wedged
        //    spring (a degenerate trace, a NaN) and the picture is stuck behind the player's
        //    head; snap it rather than leave them blind.
        if (camTarget.distanceToSquared(camLast) > CAM_WEDGE_EPS * CAM_WEDGE_EPS) {
          camMovedAt = timeMs;
        } else if (speed > 0.5 && timeMs - camMovedAt > CAM_WEDGE_MS) {
          camPivot.set(wantX, wantY, wantZ);
          camPivotVel.set(0, 0, 0);
          camDist = input.camDist;
          camDistVel = 0;
          camWall = CAM_DIST_MAX;
          camWallVel = 0;
          camMovedAt = timeMs;
          console.warn('[stillsun] camera boom wedged; snapped');
        }
        camLast.copy(camTarget);

        camera.position.copy(camTarget);
        // Look back down the boom at the pivot: the same YXZ convention `aimDirection` uses,
        // with the pitch negated because a positive boom elevation looks *down*. No roll — a
        // third-person camera that rolls reads as a bug, not as bracing.
        camera.rotation.set(-input.camPitch, input.camYaw, 0, 'YXZ');
      }

      camera.updateMatrixWorld();

      // Footsteps.
      if (!dead) {
        const sprinting = (sample.buttons & Buttons.Sprint) !== 0;
        const crouched = stance === Stance.Crouch;
        const surface = surfaceAt(feet, map.world);
        // A foot plants on every half-cycle of the stride clock above. (It used to ride the
        // weapon bob's own plants, which went with the view model.) A plant reported while
        // airborne is dropped: the landing thump is the landing's own sound.
        const stride = Math.floor(bobPhase / Math.PI);
        if (stride !== prevStride) {
          prevStride = stride;
          if (state.grounded) {
            sound.footsteps.step(speed, surface, sprinting, crouched);
            // …and a print in the ground under that foot. `stride` already alternates, so its
            // parity *is* which foot landed; no extra state. The trail fades over eight paces
            // (render/decals.ts), which is the length the owner asked for.
            decals.footprint(feet, input.bodyYaw, map.world, (stride & 1) === 0);
          }
        }
        // Rain on plating, from the same surface flag: standing on the hull is the cheapest
        // honest proxy for "there is steel over your head", and it is the one the footsteps
        // already paid for. Out on the dunes the drips go silent and the bed carries it.
        sound.setNearHull(surface === 'metal' ? 1 : 0);
      }

      prevGrounded = state.grounded;
      prevVelY = state.vel.y;
    }

    const serverNow = net.serverNow();
    // No `dt` and no camera: remote bodies are crowd instances, and the crowd advances every
    // clip in the world in one pass of its own further down this frame.
    remotes.update(serverNow);
    entities.update(serverNow, dt, camera.position);
    lootBeams.update(dt);
    containers.update(dt);
    // Footprints, and the burn marks a fight leaves. The prints fade over eight paces; the
    // rest of the field is permanent for the session and this call does nothing for it.
    decals.update(dt);
    // One camera matrix update a frame. The block above already composed it immediately after
    // writing the camera, on every frame the player has control; this covers the paths that do
    // not — the hub, the death screen, a kill cam, the dropship — where the camera is written by
    // somebody else and nothing has composed it yet.
    if (!input.active) camera.updateMatrixWorld();
    beacons.update(dt, camera, feet);
    questMarker.update(dt, camera, feet);
    // Her idle, her lantern, her plate, and the one line she says when you first come close.
    // The last argument gates only the speaking: she is seeded (and precompiled) at deploy, and
    // `feet` is still the previous raid's prediction until Welcome lands. Same pair of
    // conditions the WoW bar uses to decide the world is really there.
    npcs.update(dt, camera, feet, welcomed && !cinematic.covering);
    // The crowd. `?crowd=N` seeds N dummy citizens on the first frame the player is really
    // standing somewhere — the gate in docs/PERF-BUDGET.md — and then this is the whole
    // per-frame cost of every character in the world: one pass, no allocation, 18 draws.
    if (crowd !== null) {
      if (crowdDebugCount > 0 && welcomed && !cinematic.covering && crowdDebugMap !== map.id) {
        crowdDebugMap = map.id;
        // Give back the previous world's dummies by slot rather than calling `crowd.clear()`:
        // that would also release the slots the remotes, the monsters and the NPCs are holding,
        // and their stored ids would then alias other people's bodies.
        for (const slot of crowdDebugSlots) crowd.release(slot);
        // Centred a radius *ahead* of the player, not on them: the budget's row is "300 visible",
        // and a ring around the camera can never have more than half of it in the frustum. This
        // puts nearly all of them on screen, which is what the number is supposed to mean.
        const yaw = input.bodyYaw;
        crowdDebugSlots = crowd.debugFill(
          crowdDebugCount,
          {
            x: feet.x - Math.sin(yaw) * crowdDebugRadius,
            y: feet.y,
            z: feet.z - Math.cos(yaw) * crowdDebugRadius,
          },
          crowdDebugRadius,
        );
        console.info(
          `[stillsun] crowd gate: ${String(crowdDebugSlots.length)} citizens within ` +
            `${String(crowdDebugRadius)} m of the spawn in ${map.id}`,
        );
      }
      crowd.update(dt, camera, feet, timeMs, hud.canvas.clientWidth, hud.canvas.clientHeight);
    }
    quests.update(dt * 1000);
    // The zone map. Self-throttled to 10 Hz and a no-op while it is closed, so this costs one
    // branch on the frames it is not up.
    zoneMap.update(dt * 1000);
    // Our own bag: dropped on death, and rubbed out once we have walked back to the spot and
    // found nothing there (somebody else took it, or we did). Only ever runs while a mark exists.
    if (bagPos !== null) {
      const near = Math.hypot(feet.x - bagPos.x, feet.z - bagPos.z) < 6;
      if (near && entities.nearestBag(bagPos, 3) === null) {
        bagPos = null;
        zoneMap.setBag(null);
      }
    }
    // The world clock. Six in-game days pass in a real one (packages/shared/src/worldclock.ts),
    // and this is the whole of it: sun angle, the four-stop colour ramp, and the two lights'
    // colour and intensity. Uniform writes — never a light count, never a `visible` toggle
    // (WEBGPU-NOTES §3), so a sunset costs the same as noon.
    sceneHandle.updateSky(Date.now());
    // Grass wind, and the fade origin the field is drawn around. Two uniform writes; the blades
    // themselves never touch the CPU. It follows the *body*, not the camera — a third-person
    // boom sits several metres back and would drag the field with it.
    sceneHandle.grass?.update(dt, grassOrigin.set(feet.x, feet.y, feet.z));
    // The life layer: lantern flicker, banner cloth, the carrion flock and the Choir's camp
    // loops. Two uniform writes and a distance check over ten camps; nothing per instance.
    // It takes the *body* position for the same reason grass does — the audio listener and the
    // Choir's "audible before visible" range are about where the player is, not where the boom
    // is — and `keyIntensity` as the time of day (1 at noon, 0 in deep night).
    sceneHandle.life?.update(dt, grassOrigin, sceneHandle.sky.keyIntensity);

    // --- audio --------------------------------------------------------------
    // The listener sits at the *body's* eye with the *camera's* orientation. Putting it at the
    // camera would place the player's ears twelve metres behind their head and make the whole
    // world sound distant; taking the orientation from the body would make a spin round the
    // character swing the mix for no reason the player can see.
    sound.update(dt);
    camera.getWorldDirection(listenerForward);
    listenerUp.copy(camera.up).applyQuaternion(camera.quaternion);
    listenerPos.set(feet.x, feet.y + eyeHeight, feet.z);
    sound.setListener(listenerPos, listenerForward, listenerUp);

    // --- ability effects --------------------------------------------------
    // The reveal set is only gathered while a Pulse window is actually open; the rest of
    // the time this is one boolean and no iteration at all.
    let revealCount = 0;
    if (effects.revealActive) {
      revealCount = entities.revealPoints(revealScratch);
      revealCount = remotes.revealPoints(revealScratch, revealCount);
    }
    effects.update(dt, revealScratch, revealCount, resolvePosition);

    // --- interact scan ----------------------------------------------------
    // Containers live in the map file, loot bags are entities; whichever is closer wins,
    // and the id space (bags are >= 40000) is never used to tell them apart — the kind is.
    if (input.active && !dead && timeMs - lastInteractAt >= INTERACT_SCAN_MS) {
      lastInteractAt = timeMs;
      // A downed squadmate outranks any container: nothing in a locker matters more, and
      // the two verbs share the E key.
      reviveTarget = downed ? null : remotes.nearestDowned(feet, REVIVE_RANGE);
      const box = reviveTarget ? null : containers.nearest(feet, INTERACT_RANGE);
      const bag = reviveTarget ? null : entities.nearestBag(feet, INTERACT_RANGE);
      // A person outranks both, and by a distance the player cannot game: her reach is
      // NPC_TALK_RANGE, the same number the server validates the Talk against. Standing on a
      // loot bag at her feet must not cost you the only line of dialogue in the game.
      const person = reviveTarget ? null : npcs.nearest(feet);
      const pick: InteractCandidate | null = person
        ? { kind: 'npc', id: person.id, label: 'Speak', pos: person.pos }
        : box && (!bag || box.dist <= bag.dist)
          ? { kind: 'container', id: box.id, label: 'Open hull locker', pos: box.pos }
          : bag
            ? { kind: 'bag', id: bag.id, label: 'Search loot bag', pos: bag.pos }
            : null;
      candidate = pick;
      containers.setHighlight(pick?.kind === 'container' ? pick.id : 0);
      bagHighlight = pick?.kind === 'bag' ? pick.id : 0;
      refreshHighlight();
      overlay.setPrompt(pick === null ? '' : pick.label);
      hud.setInteractAvailable(pick !== null || reviveTarget !== null);
    } else if (dead || !input.active) {
      if (candidate !== null || reviveTarget !== null) {
        candidate = null;
        reviveTarget = null;
        containers.setHighlight(0);
        bagHighlight = 0;
        hoverId = 0;
        refreshHighlight();
        overlay.setPrompt('');
        hud.setInteractAvailable(false);
      }
    }

    // --- revive channel ---------------------------------------------------
    // The protocol has no revive verb, so the channel is a *held* Buttons.Interact next
    // to a downed ally and the server owns the 3 s timer; this bar only mirrors it. See
    // the M3 client report: a named `InteractAction.Revive = 6` is the cleaner contract.
    if (reviveTarget !== null && !dead && input.active) {
      if (interactHeld) reviveHeldSec = Math.min(REVIVE_SECONDS, reviveHeldSec + dt);
      else reviveHeldSec = Math.max(0, reviveHeldSec - dt * 2);
      hud.setRevive(reviveTarget.name, reviveHeldSec / REVIVE_SECONDS);
    } else {
      reviveHeldSec = 0;
      hud.setRevive(null, 0);
    }

    // --- feedback context -------------------------------------------------
    collector.sampleFrame(dt, timeMs);
    collector.live.rtt = net.rtt;
    collector.live.pos = { x: feet.x, y: feet.y, z: feet.z };

    // --- hover highlight ---------------------------------------------------
    // The cursor is always live, so what is under it is asked on a clock rather than on every
    // mousemove: 20 Hz is under the eye's notice and costs one ray.
    if (hoverPending && input.active && !dead && timeMs - lastHoverAt >= HOVER_INTERVAL_MS) {
      lastHoverAt = timeMs;
      hoverPending = false;
      const over = pickAt(hoverAtX, hoverAtY);
      // The selected target keeps its own ring; the hover is only ever the *other* one.
      hoverId = over === input.targetId ? 0 : over;
      refreshHighlight();
      hud.canvas.style.cursor = over !== 0 ? 'pointer' : '';
    }

    // --- the local body ----------------------------------------------------
    // The crowd renderer draws every character from a per-character state, the local player
    // included (plan §1a). This is the one place that state is assembled.
    publishLocalBody();

    // --- HUD --------------------------------------------------------------
    hud.tickEffects(dt, timeMs);
    hud.setHealth(health, MAX_HP);
    hud.updateMovement(predictor.state, timeMs);
    // --- the WoW bar ------------------------------------------------------
    abilities.tick(dt * 1000);
    killFeed.tick(timeMs);
    //  is the loading plate: while it is up the world behind it is half-built, and
    // a HUD drawn over a loading screen is the exact confusion the plate exists to remove.
    const barOn = input.active && !dead && !killCam.active && !hub.visible && !cinematic.covering;
    raidBar.setVisible(barOn);
    // The party column and the world clock follow the bar exactly: both belong to the world,
    // and neither should be legible over the hub or a loading plate.
    partyPanel.setVisible(barOn);
    hud.updateWorldClock(barOn, timeMs);
    if (barOn) {
      // One reused view object filled six times: the bar reads it and keeps no reference, so
      // the whole ability row costs no allocation at all.
      slotView.stamina = predictor.state.stamina;
      slotView.gcdFraction = abilities.gcdFraction;
      // Six slots on the bar (1-6). Slots the Calling has not authored draw as empty cells.
      for (let slot = 0; slot < 6; slot++) {
        slotView.fraction = abilities.fraction(slot);
        slotView.remainingMs = abilities.remainingMs(slot);
        slotView.def = abilities.def(slot);
        slotView.ready = abilities.ready(slot);
        slotView.short = abilities.short(slot);
        // A cooldown ending is one of the halo's wake events: the ring says "that is
        // back" on the frame it becomes true, which is the moment worth telling.
        const offCooldown = raidBar.updateAbility(slot, slotView);
        if (offCooldown) hud.wakeHalo();
        // The halo's pips are a "that is back" instrument, so they read the slot's own
        // cooldown and its price — never the global cooldown, which would blink the whole
        // ring dark for a second and a half after every single press.
        hud.setHaloPip(
          slot,
          slotView.def !== undefined && slotView.remainingMs <= 0 && !slotView.short,
        );
      }
      raidBar.setQuickSlots(inventory);
      // Rage or mana, and the local cast bar. Both come off S2C.Progress (v3.1 §9.1) through
      // the tracker, which counts the cast down at frame rate between messages.
      raidBar.setResource(abilities.resourceKind, abilities.resource, abilities.resourceMax);
      raidBar.setCast(abilities.castDef, abilities.castMs, abilities.castFraction);
      raidBar.setAutoAttack(autoAttackOn);
    }
    // Surge, Veil and Silence went off the wire with the shooter's Callings (v3 §3.1), and the
    // screen tint that read them goes with them rather than being re-pointed at `Casting`: a
    // cast bar wearing Surge's paint is a wrong answer, not a placeholder. Phase 2a gives
    // `SnapFlags.Casting` and `InCombat` their own treatment.
    hud.setAbilityGrade(null);
    if (downed) {
      hud.setDowned(
        Math.max(0, (downedUntil - performance.now()) / 1000),
        DOWNED_DURATION_MS / 1000,
      );
    } else {
      hud.setDowned(null, DOWNED_DURATION_MS / 1000);
    }
    hud.setBoss(input.active ? entities.boss() : null);
    // --- the target frame -------------------------------------------------
    // `input.targetId` is the single source of truth (see the targeting section): whatever it
    // names, the frame draws — a Hymn out of the entity manager, another Waker out of the
    // remotes, or our own body, which a Warden selects to heal themselves.
    updateTargetFrame(barOn ? input.targetId : 0, timeMs);

    // --- the halo ---------------------------------------------------------
    // A live-raid instrument: the hub is not the raid, the death screen is the end of one,
    // and the kill cam is somebody else's eyes — our bearings have no business in any of
    // them. `feet` is the predicted local position, refreshed at the top of this frame.
    // The ring is gone down the sights, so ADS is written before the state is asked for.
    const haloOn = input.active && !dead && !killCam.active && !hub.visible && !cinematic.covering;
    hud.setAiming(aiming);
    const wakerCount = haloOn ? remotes.compassTargets(compassScratch) : 0;
    hud.updateHalo(
      haloOn,
      timeMs,
      health,
      health / MAX_HP,
      predictor.state.stamina / MAX_STAMINA,
      // The compass ring reads the camera, the way a minimap does: it answers "which way am
      // I looking", not "which way are my boots pointing".
      input.camYaw,
      feet,
      compassScratch,
      wakerCount,
    );

    // The kill cam is a beat *before* the death screen: no countdown, and in a raid no
    // "loot lost" panel, until it has handed back. `respawnAt` was pushed out to match.
    if (dead && !killCam.active) {
      const left = (respawnAt - performance.now()) / 1000;
      if (mode === 'raid') {
        // A raid death is final: the server drops the player's bag and removes them, so
        // the countdown is a beat before the run ends rather than a respawn timer.
        hud.setRespawnCountdown(left);
        if (left <= 0 && !raidOver) {
          raidOver = true;
          hud.hideDeath();
          collector.logEvent('died in raid');
          overlay.showLost('Your backpack is on the ground where you fell. Loot lost.');
        }
      } else {
        hud.setRespawnCountdown(left);
      }
    }

    if (timeMs - lastHudAt >= HUD_INTERVAL_MS) {
      lastHudAt = timeMs;
      hud.setStats({
        fps,
        ping: net.rtt,
        tick,
        players: remotes.count + (welcomed ? 1 : 0),
        backend: jitOff ? `${gfx.backend} · jit off` : gfx.backend,
      });
    }
    // Rebuilding the scoreboard DOM is only worth doing while it is on screen.
    if (timeMs - lastScoreAt >= 500) {
      lastScoreAt = timeMs;
      hud.setScores(scoreRows(), selfId);
    }

    // --- render -----------------------------------------------------------
    // The one world-matrix walk of the frame (`scene.matrixWorldAutoUpdate` is off, see
    // render/scene.ts): every system above has moved what it moves, and the passes inside
    // `post.render()` — colour, three cascades, the chain — all read the result.
    scene.updateMatrixWorld();
    post.render();

    // Pixel reads must happen here — inside the same animation-frame callback as the
    // render, before the browser composites and the drawing buffer is gone.
    capture.drain();

    // The hand-off happens here and nowhere else: after a real render, counted in real frames.
    // `precompileScene` parks this loop for its whole duration, so a frame reaching this line is
    // itself the proof that the compile is over and the world is drawable — and by the third one
    // the player is standing on the ground rather than falling through a half-built map.
    if (cinematic.covering && !revealing && deployFramesLeft > 0) {
      deployFramesLeft--;
      if (deployFramesLeft === 0) {
        revealing = true;
        // The plate cuts straight to the player standing in the world: with the flyby gone
        // (plan §1d) there is nothing left for it to hand over to.
        void cinematic.reveal();
      }
    }
  };

  // Park the camera somewhere presentable before the player enters, so the
  // dusk scene is visible behind the start modal.
  const spawn = spawnOf(map);
  camera.position.set(spawn.x, spawn.y + PLAYER_EYE_HEIGHT + 2, spawn.z + 6);
  camera.rotation.set(-0.05, 0.35, 0, 'YXZ');
  camera.fov = settings.current.fov;
  camera.updateProjectionMatrix();

  hud.setStats({ fps: 0, ping: 0, tick: 0, players: 0, backend: gfx.backend });
  hud.setHealth(MAX_HP, MAX_HP);
  void renderer.setAnimationLoop(frame);

  // Expose a tiny handle for browser-console poking during M1 bring-up.
  Object.defineProperty(window, 'stillsun', {
    value: {
      net,
      hud,
      // The raid HUD, for screenshots and console poking: `raidBar.setMenuOn('bag', true)`,
      // `killFeed.debugRow({...})`, `hud.forceHaloState('awake')`.
      raidBar,
      killFeed,
      // The zone map (M). Exposed so a probe can open it and stand the reader anywhere on the
      // Spine — `zoneMap.forcePose({ pos: { x: 8, y: 7, z: 78 }, yaw: 0.1 })` — without walking
      // a player 200 m, the same way `hud.forceHaloState` freezes the ring for a shot.
      zoneMap,
      input,
      overlay,
      // The local body the crowd renderer draws us from, so a probe can read what the frame
      // loop last published without waiting on a snapshot.
      localBodyState,
      hub,
      // The Waking, and the in-raid quest tracker: both are on the shot list for the
      // onboarding probes, and neither can be reached from the console any other way.
      waking,
      quests,
      session,
      // The live progress cache. Exposed so a probe can drive the hub's unlock ladder against
      // the *mounted* store rather than a second module instance imported at the console.
      progressStore,
      collector,
      capture,
      // The crowd. Exposed so `tools/scripts/perf-probe.mjs` can read the draw and instance
      // counts the budget is written against without inferring them from a screenshot.
      crowd,
      entities,
      // M6 gear-drop beams, exposed so `tools/scripts/gear-shots.mjs` can assert what the scene
      // actually holds rather than inferring a rarity beam from a screenshot.
      lootBeams,
      // Remote bodies, exposed so `tools/scripts/armor-shots.mjs` can stand a rank of Wakers
      // in known armour in front of the camera without a second client in the room.
      remotes,
      // The people standing on the map, exposed so `tools/scripts/npc-shots.mjs` can frame each
      // of them from their own authored yaw and drive a bark without walking a player 200 m.
      npcs,
      beacons,
      containers,
      abilities,
      effects,
      killCam,
      get predictor() {
        return predictor;
      },
      // A getter, not the value: `map` is reassigned on every deploy, and a probe that asked
      // which world it was standing in would otherwise be told the yard forever.
      get map() {
        return map;
      },
      /**
       * A deploy without the hub. `tools/scripts/perf-probe.mjs` picks the gate's map through
       * this rather than by clicking a rail whose markup is still being redesigned — a perf gate
       * that breaks every time a button moves is a gate nobody runs.
       */
      deploy: (mapId: string, mode: HubDeployRequest['mode'] = 'expedition'): void => {
        startDeploy({ mode, mapId, squad: '' });
      },
      gfx,
      // Exposed so a post A/B can be driven off the console without waiting on rAF (which a
      // backgrounded tab pauses): `post.render()` in a loop, fenced with
      // `gfx.renderer.backend.device.queue.onSubmittedWorkDone()`, measures GPU time directly.
      post,
      tickRate: TICK_RATE,
    },
    configurable: true,
  });
}

boot().catch((err: unknown) => {
  console.error('[stillsun] boot failed', err);
});
